// Phase 12: the planning tabs, for a real couple.
// Editors for vendors, the budget, the timeline and the couple's profile; the remove/restore safety net;
// and the read-only versions the couple sees in their portal. Logic lives in _shared/planning.js.
import { useState, useEffect } from "react";
import { Dialog, StatusTag, Note, Meter, Meta, SafeLink, Field, Icon, statusClass } from "./ui.jsx";
import { getActiveStore } from "./lib/store.js";
import { DocFileLink } from "./cloud-ui.jsx";
import {
  VENDOR_STATUSES, vendorStatusLabel, blankVendor, isCommitted, logContact, budgetSummary, blankExpense,
  buildMilestones, sortMilestones, prettyTime, timeValue, sortRunOfShow, normalizeDayOf, TIMELINE_LABELS, addMinutes, minutesBetween, prettyRange, durationLabel, namesMatch, restorable,
  PEOPLE_ROLES, PEOPLE_SIDES, sortPeople, normalizeBudgets, categoryTargets, overCap, docCategory,
  normalizeLayouts, checkLayoutFile, isImageFile, LAYOUTS_MAX, INSPIRATION_CATEGORIES, normalizeLink, PINS_MAX
} from "../supabase/functions/_shared/planning.js";
import { EXTRACT_DOC_TYPES } from "../supabase/functions/_shared/extract.js";
import { feeSummary } from "../supabase/functions/_shared/fees.js";

// Negative amounts read "−$4,800", never "$-4,800".
export const fmt = n => { const v = Math.round(Number(n) || 0); return (v < 0 ? "−$" : "$") + Math.abs(v).toLocaleString("en-US"); };
const fmtCents = n => { const v = Number(n) || 0; return (v < 0 ? "−$" : "$") + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 }); };
export const isoDay = v => {
  const s = String(v || "").trim();
  if (!s || s === "—") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
export const prettyDay = iso => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return iso || "";
  const [y, m, d] = iso.split("-").map(Number);
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] + " " + d + ", " + y;
};
const shortDay = iso => prettyDay(iso).replace(/, \d{4}$/, "");
const EditIcon = <><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></>;
const TrashIcon = <><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></>;
const CheckLabel = ({ checked, onChange, children }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, cursor: "pointer", minHeight: 44 }}>
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
    <span>{children}</span>
  </label>
);
export const EmptyRow = ({ cols, children }) => <tr className="empty-row"><td colSpan={cols} className="empty" style={{ padding: "16px 18px" }}>{children}</td></tr>;

/* ───────────── Remove a couple: type their names to confirm (Step 1) ───────────── */

export function RemoveCoupleDialog({ client, signed, onConfirm, onClose }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const okToGo = namesMatch(typed, client.names);
  const go = async () => { if (!okToGo) return; setBusy(true); setErr(""); try { await onConfirm(); onClose(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  return (
    <Dialog title={"Remove " + client.names + "?"} kicker="Clients" onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Keep them</button><button className="btn btn-primary" disabled={!okToGo || busy} onClick={go}>{busy ? "Removing…" : "Remove couple"}</button></>}>
      <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55 }}>
        Their profile, tasks, vendors, budget, timeline, documents, quote, agreement and payments are taken off the dashboard for everyone on the team, and their portal link stops working.
      </p>
      <p style={{ margin: 0, fontSize: 16.5, lineHeight: 1.55 }}>You can bring them back from <strong>Settings → Recently removed</strong> for 30 days. After that they&rsquo;re gone for good.</p>
      {signed && <Note tone="warn">Their agreement is signed. It stays in the 30-day copy, but download it from Documents if you want your own.</Note>}
      <Field label={"Type “" + client.names + "” to confirm"}>
        <input className="input" value={typed} onChange={e => setTyped(e.target.value)} onKeyDown={e => { if (e.key === "Enter") go(); }} autoComplete="off" />
      </Field>
      {err && <Note tone="warn"><span role="alert">{err}</span></Note>}
    </Dialog>
  );
}

export function RecentlyRemoved({ removed, today, onRestore }) {
  const list = restorable(removed, today);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  return (
    <section className="stack" style={{ "--gap": "12px" }} aria-labelledby="recently-removed-h">
      <h3 id="recently-removed-h" className="label" style={{ margin: 0 }}>Recently removed</h3>
      <p className="field-hint" style={{ margin: 0 }}>Couples you removed in the last 30 days, with everything filed under them. Restoring brings it all back. Their old portal link stays off; make a new one from the Couple portal button.</p>
      {list.length === 0 && <div className="empty">Nobody removed in the last 30 days.</div>}
      {list.map(r => {
        const left = 30 - Math.max(0, Math.round((Date.parse(today) - Date.parse(isoDay(r.removedAt))) / 86400000));
        return (
          <div key={r.client.id} className="list-row" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 500 }}>{r.client.full || r.client.names}</div>
              <div className="label" style={{ marginTop: 3 }}>Removed {prettyDay(isoDay(r.removedAt))}{r.by ? " by " + r.by : ""} · {left} {left === 1 ? "day" : "days"} left</div>
            </div>
            <button className="btn btn-secondary" disabled={!!busy} onClick={async () => { setBusy(r.client.id); try { await onRestore(r); setNote(r.client.names + " are back on the Clients screen."); } finally { setBusy(""); } }}>
              {busy === r.client.id ? "Restoring…" : "Restore " + r.client.names}
            </button>
          </div>
        );
      })}
      {note && <div className="field-hint" role="status">{note}</div>}
    </section>
  );
}

/* ───────────── Wedding colors (Batch C: more choices, grouped) ───────────── */

export const COLOR_GROUPS = [
  { name: "Neutrals", colors: [{ name: "Ivory", hex: "#f5ead8" }, { name: "Champagne", hex: "#e8d9bd" }, { name: "Greige", hex: "#b9b0a3" }, { name: "Taupe", hex: "#a19786" }, { name: "Charcoal", hex: "#474238" }, { name: "Black", hex: "#141414" }] },
  { name: "Earth", colors: [{ name: "Terracotta", hex: "#c67139" }, { name: "Clay", hex: "#d67f48" }, { name: "Rust", hex: "#9c4a24" }, { name: "Espresso", hex: "#8c491a" }, { name: "Mustard", hex: "#c9a03d" }] },
  { name: "Greens", colors: [{ name: "Sage", hex: "#7a8a5e" }, { name: "Soft sage", hex: "#ccdbb2" }, { name: "Olive", hex: "#6b6b3a" }, { name: "Eucalyptus", hex: "#9fb5a8" }, { name: "Emerald", hex: "#2f6b4f" }] },
  { name: "Blues", colors: [{ name: "Dusty blue", hex: "#8fa8bf" }, { name: "Slate", hex: "#5b6f82" }, { name: "Navy", hex: "#23324a" }] },
  { name: "Pinks & Berries", colors: [{ name: "Blush", hex: "#e8c4be" }, { name: "Dusty rose", hex: "#c08f8a" }, { name: "Mauve", hex: "#9e7a86" }, { name: "Burgundy", hex: "#6e1f2c" }] },
  { name: "Jewel", colors: [{ name: "Plum", hex: "#5a2e4f" }, { name: "Sapphire", hex: "#2c4a7a" }, { name: "Teal", hex: "#2e6e6e" }] },
  { name: "Metallics", colors: [{ name: "Gold", hex: "#c2a25f" }, { name: "Rose gold", hex: "#c99a86" }, { name: "Silver", hex: "#b8b8b8" }] }
];
export const ALL_COLORS = COLOR_GROUPS.flatMap(g => g.colors);
export const colorName = hex => (ALL_COLORS.find(c => c.hex.toLowerCase() === String(hex || "").toLowerCase()) || {}).name || hex;

/* ───────────── The couple's whole profile (Step 1: Clients) ───────────── */

export function ProfileDialog({ client, onSave, onClose }) {
  const [f, setF] = useState({
    location: client.location || "", budget: client.budget ? String(client.budget) : "", engaged: client.engaged && client.engaged !== "—" ? client.engaged : "",
    preferred: client.preferred || "Email", story: client.story || "", theme: client.theme || "",
    colors: Array.isArray(client.colors) ? client.colors.slice(0, 6) : [], newColor: "#a19786",
    boards: (client.boards || []).join("\n"), must: (client.must || []).join(", "), cant: (client.cant || []).join(", "),
    dietary: client.dietary || "", accessibility: client.accessibility || "", family: client.family || "", requests: client.requests || ""
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggleColor = hex => setF(p => ({ ...p, colors: p.colors.includes(hex) ? p.colors.filter(c => c !== hex) : p.colors.length >= 6 ? p.colors : [...p.colors, hex] }));
  const save = () => {
    const commas = s => String(s || "").split(",").map(x => x.trim()).filter(Boolean);
    onSave({
      location: f.location.trim(), budget: Math.max(0, Number(String(f.budget).replace(/[$,\s]/g, "")) || 0), engaged: f.engaged.trim() || "—",
      preferred: f.preferred, story: f.story.trim(), theme: f.theme.trim(), colors: f.colors,
      boards: f.boards.split("\n").map(x => x.trim()).filter(Boolean), must: commas(f.must), cant: commas(f.cant),
      dietary: f.dietary.trim(), accessibility: f.accessibility.trim(), family: f.family.trim(), requests: f.requests.trim()
    });
    onClose();
  };
  return (
    <Dialog title={"Profile · " + client.names} width={720} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}>Save profile</button></>}>
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">The basics</div>
        <div className="grid-2">
          <Field label="Where (city, region)"><input className="input" value={f.location} onChange={e => set("location", e.target.value)} /></Field>
          <Field label="Total wedding budget" hint="Their whole budget, not your fee. Leave blank if they haven't said."><input className="input" inputMode="decimal" value={f.budget} onChange={e => set("budget", e.target.value)} /></Field>
          <Field label="Engaged"><input className="input" value={f.engaged} placeholder="e.g. Dec 24, 2025" onChange={e => set("engaged", e.target.value)} /></Field>
          <Field label="Best way to reach them">
            <select className="input" value={f.preferred} onChange={e => set("preferred", e.target.value)}>{["Email", "Text", "Phone"].map(o => <option key={o}>{o}</option>)}</select>
          </Field>
        </div>
      </section>
      <hr className="rule" />
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">Their vision</div>
        <Field label="The day they're picturing"><textarea className="input" value={f.story} onChange={e => set("story", e.target.value)}></textarea></Field>
        <Field label="Theme or feel"><input className="input" value={f.theme} onChange={e => set("theme", e.target.value)} /></Field>
        <Field label="Colors" hint="Up to six.">
          <div className="stack" style={{ "--gap": "10px" }}>
            {COLOR_GROUPS.map(g => (
              <div key={g.name} className="stack" style={{ "--gap": "6px" }}>
                <div className="field-hint" style={{ margin: 0 }}>{g.name}</div>
                <div className="row" style={{ "--gap": "6px" }}>
                  {g.colors.map(c => (
                    <button key={c.hex} type="button" className={"chip chip-sm" + (f.colors.includes(c.hex) ? " active" : "")} aria-pressed={f.colors.includes(c.hex)} onClick={() => toggleColor(c.hex)}>
                      <span className="swatch sm" style={{ background: c.hex, borderColor: "var(--rule-strong)", marginRight: 6 }}></span>{c.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="row" style={{ "--gap": "8px" }}>
              <span className="field-hint" style={{ margin: 0 }}>Chosen:</span>
              {f.colors.length ? f.colors.map(hex => (
                <button key={hex} type="button" className="chip chip-sm" onClick={() => toggleColor(hex)} aria-label={"Remove color " + hex}>
                  <span className="swatch sm" style={{ background: hex, marginRight: 6 }}></span>{hex} ×
                </button>
              )) : <span className="text-muted">None yet</span>}
            </div>
            <div className="row" style={{ "--gap": "8px" }}>
              <input type="color" className="color-input" aria-label="Pick another color" value={f.newColor} onChange={e => set("newColor", e.target.value)} />
              <button type="button" className="btn btn-secondary" onClick={() => toggleColor(f.newColor)} disabled={f.colors.includes(f.newColor) || f.colors.length >= 6}>Add this color</button>
            </div>
          </div>
        </Field>
        <Field label="Inspiration links" hint="One per line."><textarea className="input" value={f.boards} onChange={e => set("boards", e.target.value)}></textarea></Field>
        <div className="grid-2">
          <Field label="Must-haves" hint="Comma separated."><input className="input" value={f.must} onChange={e => set("must", e.target.value)} /></Field>
          <Field label="Not for them" hint="Comma separated."><input className="input" value={f.cant} onChange={e => set("cant", e.target.value)} /></Field>
        </div>
      </section>
      <hr className="rule" />
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">People and needs</div>
        <Field label="Dietary"><textarea className="input" value={f.dietary} onChange={e => set("dietary", e.target.value)}></textarea></Field>
        <Field label="Accessibility"><textarea className="input" value={f.accessibility} onChange={e => set("accessibility", e.target.value)}></textarea></Field>
        <Field label="Family notes"><textarea className="input" value={f.family} onChange={e => set("family", e.target.value)}></textarea></Field>
        <Field label="Special requests"><textarea className="input" value={f.requests} onChange={e => set("requests", e.target.value)}></textarea></Field>
      </section>
    </Dialog>
  );
}

/* ───────────── Vendors (Step 2) ───────────── */

const toForm = v => ({
  name: v.name || "", category: v.category || "", contractStatus: v.contractStatus || "Considering",
  contact: v.contact && v.contact !== "—" ? v.contact : "", phone: v.phone && v.phone !== "—" ? v.phone : "", email: v.email && v.email !== "—" ? v.email : "",
  website: v.website && v.website !== "—" ? v.website : "", address: v.address && v.address !== "—" ? v.address : "",
  quote: v.quote ? String(v.quote) : "", range: v.range && v.range !== "—" && v.range !== "Awaiting decision" ? v.range : "",
  contractSigned: isoDay(v.contractSigned), keyTerms: v.keyTerms || "",
  deposit: v.deposit ? String(v.deposit) : "", depositDue: isoDay(v.depositDue), depositPaid: !!v.depositPaid,
  final: v.final ? String(v.final) : "", finalDue: isoDay(v.finalDue), finalPaid: !!v.finalPaid,
  method: v.method && v.method !== "—" ? v.method : "", services: (v.services || []).join(", "), notes: v.notes || ""
});
const moneyIn = s => Math.max(0, Math.round((Number(String(s || "").replace(/[$,\s]/g, "")) || 0) * 100) / 100);

/** Applies the vendor form; stamps when a payment was marked paid (the weekly report counts those). */
export function applyVendorForm(v, f, now = new Date().toISOString()) {
  const quote = moneyIn(f.quote), deposit = moneyIn(f.deposit);
  let final = moneyIn(f.final);
  if (!final && quote && deposit && quote > deposit && !f.final) final = Math.round((quote - deposit) * 100) / 100;
  return {
    ...v, name: f.name.trim(), inv: f.name.trim().slice(0, 2).toUpperCase(), category: f.category,
    contractStatus: f.contractStatus, contact: f.contact.trim(), phone: f.phone.trim(), email: f.email.trim(), website: f.website.trim(), address: f.address.trim(),
    quote, range: f.range.trim(), contractSigned: f.contractSigned || "", keyTerms: f.keyTerms.trim(),
    deposit, depositDue: prettyDay(f.depositDue), depositPaid: !!f.depositPaid, depositPaidAt: f.depositPaid ? (v.depositPaid ? v.depositPaidAt || null : now) : null,
    final, finalDue: prettyDay(f.finalDue), finalPaid: !!f.finalPaid, finalPaidAt: f.finalPaid ? (v.finalPaid ? v.finalPaidAt || null : now) : null,
    method: f.method.trim(), services: f.services.split(",").map(s => s.trim()).filter(Boolean), notes: f.notes.trim()
  };
}

/* ───────────── Tasks shared by the Tasks tab, the vendor row and the vendor card (Phase 13 · Batch C) ───────────── */

export const TASK_STATUS_LABEL = { incomplete: "Incomplete", "in-progress": "In progress", complete: "Complete" };
export const TASK_NEXT_STATUS = { incomplete: "in-progress", "in-progress": "complete", complete: "incomplete" };
const taskStatus = t => TASK_STATUS_LABEL[t.status] ? t.status : "incomplete";

/** The square tick: Incomplete → In progress → Complete → Incomplete. */
export function TaskCheck({ task, onCycle, small }) {
  const st = taskStatus(task);
  return (
    <button
      type="button"
      className={"task-check" + (small ? " task-check-sm" : "") + (st === "complete" ? " done" : st === "in-progress" ? " partial" : "")}
      onClick={e => { e.stopPropagation(); onCycle(task.id); }}
      aria-label={task.title + ": " + TASK_STATUS_LABEL[st] + ". Mark as " + TASK_STATUS_LABEL[TASK_NEXT_STATUS[st]]}
      title={"Mark as " + TASK_STATUS_LABEL[TASK_NEXT_STATUS[st]]}
    >
      {st === "complete" && <Icon path={<polyline points="20 6 9 17 4 12"></polyline>} size={small ? 15 : 18} />}
    </button>
  );
}

/**
 * One vendor's tasks: tick, edit the title and due inline, and add a task in one line. The same records as the
 * Tasks tab (Vendor to-dos); nothing is copied.
 */
export function VendorTaskList({ vendor, tasks, onAdd, onUpdate, onCycle }) {
  const [title, setTitle] = useState("");
  const [edit, setEdit] = useState(null); // { id, title, due }
  const add = () => { const t = title.trim(); if (!t) return; onAdd(t); setTitle(""); };
  const saveEdit = () => { if (!edit || !edit.title.trim()) return; onUpdate(edit.id, { title: edit.title.trim(), due: edit.due.trim() }); setEdit(null); };
  return (
    <div className="vendor-tasks-list stack" style={{ "--gap": "8px" }}>
      {tasks.length === 0 && <div className="field-hint">No tasks for {vendor.name} yet.</div>}
      {tasks.map(t => edit && edit.id === t.id ? (
        <div key={t.id} className="vendor-task-row vendor-task-edit" data-escape-local="">
          <input className="input" aria-label={"Task title for " + vendor.name} value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })}
            onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") { e.preventDefault(); setEdit(null); } }} />
          <input className="input vendor-task-due" aria-label={"Due date for " + edit.title} placeholder="Due, e.g. Aug 15" value={edit.due} onChange={e => setEdit({ ...edit, due: e.target.value })}
            onKeyDown={e => { if (e.key === "Enter") saveEdit(); }} />
          <div className="row" style={{ "--gap": "6px" }}>
            <button type="button" className="btn btn-secondary" onClick={() => setEdit(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={!edit.title.trim()}>Save</button>
          </div>
        </div>
      ) : (
        <div key={t.id} className={"vendor-task-row" + (t.status === "complete" ? " is-done" : "")}>
          <TaskCheck task={t} onCycle={onCycle} small />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="vendor-task-title">{t.title}</div>
            {t.due && <div className="field-hint">Due {t.due}</div>}
          </div>
          <button type="button" className="btn btn-icon btn-ghost" aria-label={"Edit task: " + t.title} onClick={() => setEdit({ id: t.id, title: t.title, due: t.due || "" })}><Icon path={EditIcon} size={15} /></button>
        </div>
      ))}
      <div className="vendor-task-add">
        <input className="input" aria-label={"New task for " + vendor.name} placeholder="+ Add task, e.g. Confirm arrival 2 PM" value={title}
          onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === "Enter") add(); }} />
        <button type="button" className="btn btn-secondary" disabled={!title.trim()} onClick={add}>Add</button>
      </div>
    </div>
  );
}

/** Phase 13 · Step 8: "With this quote, Florist would be at $X against a budget of $Y (over by $Z)." Null when within budget. */
function overCapLine(targets, saved, form) {
  const o = overCap(targets, saved, form);
  return o && "With this quote, " + o.category + " would be at " + fmtCents(o.total) + " against a budget of " + fmtCents(o.target) + " (over by " + fmtCents(o.over) + ").";
}

export function VendorDialog({ vendor, clientId, categories, startEditing, onSave, onDelete, onClose, actor, targets,
  tasks, onAddTask, onUpdateTask, onCycleTask, docs, onAddDocument, onReadDocument, readingNote }) {
  const isNew = !vendor;
  const base = vendor || blankVendor(clientId, { category: categories[0] });
  const [editing, setEditing] = useState(isNew || !!startEditing);
  const [f, setF] = useState(toForm(base));
  const [contactNote, setContactNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = () => { if (!f.name.trim()) return; onSave(applyVendorForm(base, f), isNew); if (isNew) onClose(); else setEditing(false); };
  const logIt = () => { if (!contactNote.trim()) return; onSave(logContact(base, { note: contactNote, by: actor || "" }), false); setContactNote(""); };
  const sv = base;

  if (editing) {
    return (
      <Dialog title={isNew ? "Add vendor" : "Edit " + sv.name} kicker={isNew ? "Vendors" : sv.category} width={720} onClose={onClose}
        footer={<><button className="btn btn-secondary" onClick={isNew ? onClose : () => { setF(toForm(sv)); setEditing(false); }}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!f.name.trim()}>{isNew ? "Add vendor" : "Save vendor"}</button></>}>
        <section className="stack" style={{ "--gap": "14px" }}>
          <div className="grid-2">
            <Field label="Vendor / business name" required><input className="input" value={f.name} onChange={e => set("name", e.target.value)} /></Field>
            <Field label="Category">
              <select className="input" value={f.category} onChange={e => set("category", e.target.value)}>
                {[...new Set([f.category, ...categories].filter(Boolean))].map(c => <option key={c}>{c}</option>)}
              </select>
              {f.category === "Facilities" && <div className="field-hint">Restrooms, power, trash, tents.</div>}
            </Field>
            <Field label="Status">
              <select className="input" value={f.contractStatus} onChange={e => set("contractStatus", e.target.value)}>
                {VENDOR_STATUSES.map(s => <option key={s} value={s}>{vendorStatusLabel(s)}</option>)}
              </select>
            </Field>
            <Field label="Quote (total)"><input className="input" inputMode="decimal" value={f.quote} onChange={e => set("quote", e.target.value)} /></Field>
          </div>
          {(() => { const line = overCapLine(targets, isNew ? null : base, f); return line && <Note tone="warn">{line}</Note>; })()}
          <div className="field-hint">Booked and Contract out count toward the budget and show in the couple&rsquo;s portal.</div>
        </section>
        <hr className="rule" />
        <section className="stack" style={{ "--gap": "14px" }}>
          <div className="label">Contact</div>
          <div className="grid-2">
            <Field label="Contact person"><input className="input" value={f.contact} onChange={e => set("contact", e.target.value)} /></Field>
            <Field label="Phone"><input className="input" type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} /></Field>
            <Field label="Email"><input className="input" type="email" value={f.email} onChange={e => set("email", e.target.value)} /></Field>
            <Field label="Website"><input className="input" value={f.website} onChange={e => set("website", e.target.value)} /></Field>
          </div>
          <Field label="Address"><input className="input" value={f.address} onChange={e => set("address", e.target.value)} /></Field>
        </section>
        <hr className="rule" />
        <section className="stack" style={{ "--gap": "14px" }}>
          <div className="label">Contract</div>
          <div className="grid-2">
            <Field label="Contract signed on"><input className="input" type="date" value={f.contractSigned} onChange={e => set("contractSigned", e.target.value)} /></Field>
            <Field label="Contract dates"><input className="input" placeholder="e.g. Mar 2 – Oct 10, 2026" value={f.range} onChange={e => set("range", e.target.value)} /></Field>
          </div>
          <Field label="Key terms"><textarea className="input" value={f.keyTerms} onChange={e => set("keyTerms", e.target.value)}></textarea></Field>
        </section>
        <hr className="rule" />
        <section className="stack" style={{ "--gap": "14px" }}>
          <div className="label">Payments to this vendor</div>
          <div className="grid-3">
            <Field label="Deposit"><input className="input" inputMode="decimal" value={f.deposit} onChange={e => set("deposit", e.target.value)} /></Field>
            <Field label="Deposit due"><input className="input" type="date" value={f.depositDue} onChange={e => set("depositDue", e.target.value)} /></Field>
            <CheckLabel checked={f.depositPaid} onChange={v => set("depositPaid", v)}>Deposit paid</CheckLabel>
            <Field label="Balance"><input className="input" inputMode="decimal" value={f.final} placeholder={moneyIn(f.quote) > moneyIn(f.deposit) && moneyIn(f.deposit) ? String(moneyIn(f.quote) - moneyIn(f.deposit)) : ""} onChange={e => set("final", e.target.value)} /></Field>
            <Field label="Balance due"><input className="input" type="date" value={f.finalDue} onChange={e => set("finalDue", e.target.value)} /></Field>
            <CheckLabel checked={f.finalPaid} onChange={v => set("finalPaid", v)}>Balance paid</CheckLabel>
          </div>
          <Field label="How they're paid" hint="e.g. ACH, card, check"><input className="input" value={f.method} onChange={e => set("method", e.target.value)} /></Field>
        </section>
        <hr className="rule" />
        <section className="stack" style={{ "--gap": "14px" }}>
          <Field label="Services" hint="Comma separated."><input className="input" value={f.services} onChange={e => set("services", e.target.value)} /></Field>
          <Field label="Notes"><textarea className="input" value={f.notes} onChange={e => set("notes", e.target.value)}></textarea></Field>
        </section>
      </Dialog>
    );
  }

  const pay = (label, amt, due, paid) => (
    <tr><td>{label}</td><td data-label="Amount" className="figure">{amt ? fmtCents(amt) : "—"}</td><td data-label="Due" className="figure">{due && due !== "—" ? due : "—"}</td><td data-label="Status">{amt ? <StatusTag status={paid ? "Paid" : "Pending"} /> : "—"}</td></tr>
  );
  return (
    <Dialog title={sv.name} kicker={sv.category} width={680} onClose={onClose}
      aside={<StatusTag status={sv.contractStatus === "Signed" ? "Signed" : sv.contractStatus} style={{ marginTop: 4 }}>{vendorStatusLabel(sv.contractStatus)}</StatusTag>}
      footer={confirmDelete
        ? <><span style={{ marginRight: "auto", fontSize: 15.5 }}>Delete {sv.name} from this couple?</span><button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Keep</button><button className="btn btn-primary" onClick={() => { onDelete(sv); onClose(); }}>Delete vendor</button></>
        : <><button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => setConfirmDelete(true)}>Delete vendor</button><button className="btn btn-primary" onClick={() => setEditing(true)}>Edit vendor</button></>}>
      <div className="grid-2" style={{ "--gap": "14px 22px" }}>
        <Meta label="Contact">{sv.contact || "—"}</Meta>
        <Meta label="Phone">{sv.phone || "—"}</Meta>
        <Meta label="Email">{sv.email || "—"}</Meta>
        <Meta label="Website">{sv.website && sv.website !== "—" ? <SafeLink href={sv.website} /> : "—"}</Meta>
        <Meta label="Address" style={{ gridColumn: "1/-1" }}>{sv.address || "—"}</Meta>
      </div>
      <div className="note">
        <div className="kv">
          <span>Quote</span><span style={{ minWidth: 0 }}><span className="figure">{sv.quote ? fmtCents(sv.quote) : "—"}</span>{(() => { const line = overCapLine(targets, sv, sv); return line && <div className="field-hint" style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{line}</div>; })()}</span>
          <span>Contract</span><span>{[sv.contractSigned ? "Signed " + prettyDay(sv.contractSigned) : "", sv.range && sv.range !== "—" ? sv.range : ""].filter(Boolean).join(" · ") || "—"}</span>
          <span>Key terms</span><span>{sv.keyTerms || "—"}</span>
        </div>
      </div>
      <table className="table table-cards">
        <thead><tr><th>Payment</th><th>Amount</th><th>Due</th><th>Status</th></tr></thead>
        <tbody>{pay("Deposit", sv.deposit, sv.depositDue, sv.depositPaid)}{pay("Balance", sv.final, sv.finalDue, sv.finalPaid)}</tbody>
      </table>
      {((sv.services || []).length > 0 || (sv.addons || []).length > 0) && (
        <div>
          <div className="label" style={{ marginBottom: 8 }}>Services &amp; add-ons</div>
          <div className="row" style={{ "--gap": "6px" }}>
            {(sv.services || []).map((sx, i) => <span key={i} className="tag tag-neutral">{sx}</span>)}
            {(sv.addons || []).map((ax, i) => <span key={i} className="tag tag-outline">{ax}</span>)}
          </div>
        </div>
      )}
      {sv.notes && <div style={{ fontSize: 16 }}><div className="label">Notes</div>{sv.notes}</div>}
      <section className="stack" style={{ "--gap": "10px" }} aria-label="Contact log">
        <div className="label">Contact log{sv.lastContact && sv.lastContact !== "—" ? " · last " + sv.lastContact : ""}</div>
        <div className="row" style={{ "--gap": "8px", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 260px" }}>
            <Field label="What happened"><input className="input" value={contactNote} placeholder="e.g. Confirmed arrival at 2 PM" onChange={e => setContactNote(e.target.value)} onKeyDown={e => { if (e.key === "Enter") logIt(); }} /></Field>
          </div>
          <button className="btn btn-secondary" disabled={!contactNote.trim()} onClick={logIt}>Log contact</button>
        </div>
        {(sv.contacts || []).length === 0
          ? <div className="field-hint">Nothing logged yet. Logged contacts go into that week&rsquo;s report.</div>
          : (sv.contacts || []).slice(0, 8).map((c, i) => (
            <div key={i} className="list-row" style={{ fontSize: 15.5 }}>
              <span className="figure text-muted" style={{ fontSize: 13.5, marginRight: 10 }}>{prettyDay(isoDay(c.at))}</span>{c.note}{c.by ? <span className="text-muted"> · {c.by}</span> : null}
            </div>
          ))}
      </section>
      {onAddTask && (
        <section className="stack" style={{ "--gap": "10px" }} aria-label={"Tasks for " + sv.name}>
          <div className="label">Tasks{(tasks || []).length ? " · " + (tasks || []).filter(t => t.status !== "complete").length + " open" : ""}</div>
          <VendorTaskList vendor={sv} tasks={tasks || []} onAdd={onAddTask} onUpdate={onUpdateTask} onCycle={onCycleTask} />
        </section>
      )}
      {docs && (
        <section className="stack" style={{ "--gap": "10px" }} aria-label={"Documents for " + sv.name}>
          <div className="row" style={{ "--gap": "8px" }}>
            <div className="label">Documents</div>
            {onReadDocument && <label className="btn btn-secondary push">
              Read a document with AI
              <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onReadDocument(f); }} />
            </label>}
            {onAddDocument && <button type="button" className={"btn btn-secondary" + (onReadDocument ? "" : " push")} onClick={onAddDocument}>+ Add a document</button>}
          </div>
          {readingNote && <div className="field-hint" role="status">{readingNote}</div>}
          {docs.length === 0
            ? <div className="field-hint">No documents for {sv.name} yet. They show here and in Documents, tagged with this vendor.</div>
            : docs.map(d => (
              <div key={d.id} className="list-row row" style={{ "--gap": "6px 10px", fontSize: 15.5 }}>
                <span style={{ fontWeight: 500, minWidth: 0, overflowWrap: "anywhere" }}>{d.name}</span>
                <span className="tag tag-neutral">{docCategory(d.category)}</span>
                <StatusTag status={d.status} />
                <DocFileLink file={d.file} />
              </div>
            ))}
        </section>
      )}
    </Dialog>
  );
}

/* ───────────── Budget (Step 3) ───────────── */

const donut = (entries, total, over) => {
  const shades = inkScale(entries.length);
  const C = 2 * Math.PI * 40, gap = entries.length > 1 ? 0.8 : 0;
  let acc = 0;
  return entries.map(([label, amt], i) => {
    const frac = total ? amt / total : 0;
    const color = over && i === 0 ? "var(--oxblood)" : shades[i];
    const seg = { label, color, amt, pct: Math.round(frac * 100) + "%", dash: Math.max(0, frac * C - gap).toFixed(2) + " " + C.toFixed(2), offset: (-acc * C).toFixed(2) };
    acc += frac;
    return seg;
  });
};
function inkScale(n) {
  const a = [20, 20, 20], b = [214, 207, 196];
  return Array.from({ length: Math.max(n, 1) }, (_, i) => { const t = n <= 1 ? 0 : i / (n - 1); return "rgb(" + a.map((x, k) => Math.round(x + (b[k] - x) * t)).join(",") + ")"; });
}
export function Donut({ entries, total, over }) {
  const segs = donut(entries, total, over);
  if (!segs.length) return <div className="empty">Nothing committed yet. Booked vendors and costs you add show up here.</div>;
  return (
    <div className="row" style={{ "--gap": "20px 28px", alignItems: "center" }}>
      <svg width="168" height="168" viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)", flex: "none" }} role="img" aria-label="Committed spend by category">
        <circle cx="50" cy="50" r="40" fill="none" stroke="var(--vellum)" strokeWidth="14"></circle>
        {segs.map((g, i) => <circle key={i} cx="50" cy="50" r="40" fill="none" stroke={g.color} strokeWidth="14" strokeDasharray={g.dash} strokeDashoffset={g.offset}></circle>)}
      </svg>
      <div className="stack" style={{ "--gap": "8px", flex: "1 1 200px", minWidth: 0 }}>
        {segs.map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15.5 }}>
            <span className="swatch sm" style={{ background: l.color, borderColor: "transparent" }}></span>
            <span style={{ flex: 1, minWidth: 0 }}>{l.label}</span>
            <span className="figure">{fmt(l.amt)}</span>
            <span className="figure text-muted" style={{ width: 42, textAlign: "right", fontSize: 13.5 }}>{l.pct}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
const Stat = ({ label, value, note }) => (
  <div className="panel stat" style={{ gap: 0, padding: "20px 22px" }}>
    <div className="label">{label}</div>
    <div className="figure-lg">{value}</div>
    {note && <div className="text-muted">{note}</div>}
  </div>
);

export const EXPENSE_CATEGORIES = ["Attire & rings", "Hair & beauty", "Stationery & postage", "Favors & gifts", "License & fees", "Tips", "Travel & lodging", "Other"];

function BudgetTotalEditor({ value, onSave }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(value ? String(value) : "");
  if (!open) return <button className="btn-link" onClick={() => { setV(value ? String(value) : ""); setOpen(true); }}>{value ? "Change total budget" : "Set their total budget"}</button>;
  const save = () => { onSave(Math.max(0, Number(String(v).replace(/[$,\s]/g, "")) || 0)); setOpen(false); };
  return (
    <div className="row" style={{ "--gap": "8px", alignItems: "flex-end" }}>
      <div style={{ width: 220 }}><Field label="Total wedding budget"><input className="input" inputMode="decimal" value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") save(); }} /></Field></div>
      <button className="btn btn-primary" onClick={save}>Save</button>
      <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

function ExpenseDialog({ expense, onSave, onDelete, onClose }) {
  const isNew = !expense;
  const [f, setF] = useState({ label: expense ? expense.label : "", category: expense ? expense.category || "Other" : "Attire & rings", amount: expense ? String(expense.amount || "") : "",
    due: expense ? expense.due || "" : "", paid: expense ? !!expense.paid : false, note: expense ? expense.note || "" : "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = () => {
    if (!f.label.trim()) return;
    const next = isNew ? blankExpense(f) : { ...expense, label: f.label.trim(), category: f.category, amount: moneyIn(f.amount), due: f.due || "", note: f.note.trim(),
      paid: !!f.paid, paidAt: f.paid ? (expense.paid ? expense.paidAt || null : new Date().toISOString()) : null };
    onSave(next, isNew); onClose();
  };
  return (
    <Dialog title={isNew ? "Add a cost" : "Edit cost"} kicker="Budget" onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(expense); onClose(); }}>Delete</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.label.trim()} onClick={save}>{isNew ? "Add cost" : "Save"}</button></>}>
      <Field label="What it is" required><input className="input" value={f.label} placeholder="e.g. Wedding rings" onChange={e => set("label", e.target.value)} /></Field>
      <div className="grid-2">
        <Field label="Kind of cost"><select className="input" value={f.category} onChange={e => set("category", e.target.value)}>{EXPENSE_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Amount"><input className="input" inputMode="decimal" value={f.amount} onChange={e => set("amount", e.target.value)} /></Field>
        <Field label="Due (optional)"><input className="input" type="date" value={f.due} onChange={e => set("due", e.target.value)} /></Field>
        <CheckLabel checked={f.paid} onChange={v => set("paid", v)}>Paid</CheckLabel>
      </div>
      <Field label="Note"><input className="input" value={f.note} onChange={e => set("note", e.target.value)} /></Field>
    </Dialog>
  );
}

/* Phase 13 · Step 7: a budget for one category, as a total cap or a cap per guest. */
function CategoryBudgetDialog({ row, categories, taken, guests, onSave, onDelete, onClose }) {
  const isNew = !row;
  const choices = [...new Set([...(row ? [row.category] : []), ...categories.filter(c => !taken.has(c.toLowerCase()))])];
  const [f, setF] = useState({ category: row ? row.category : choices[0] || "", mode: row ? row.mode : "cap", amount: row ? String(row.amount || "") : "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const amount = moneyIn(f.amount);
  const ok = !!f.category && amount > 0;
  const save = () => { if (!ok) return; onSave({ category: f.category, mode: f.mode, amount }, row); onClose(); };
  return (
    <Dialog title={isNew ? "Add a category budget" : "Edit " + row.category + " budget"} kicker="Budget" onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(row); onClose(); }}>Delete</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!ok} onClick={save}>{isNew ? "Add budget" : "Save"}</button></>}>
      <Field label="Category">
        <select className="input" value={f.category} onChange={e => set("category", e.target.value)} disabled={!isNew}>
          {choices.map(c => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <fieldset className="stack" style={{ "--gap": "8px", border: 0, padding: 0, margin: 0 }}>
        <legend className="label" style={{ marginBottom: 6 }}>Kind of budget</legend>
        {[["cap", "Total cap", "e.g. $5,000 for flowers"], ["perGuest", "Per guest", "e.g. $90 a plate"]].map(([v, l, h]) => (
          <label key={v} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 16, cursor: "pointer", minHeight: 44 }}>
            <input type="radio" name="budget-mode" checked={f.mode === v} onChange={() => set("mode", v)} />
            <span>{l} <span className="text-muted" style={{ fontSize: 14.5 }}>{h}</span></span>
          </label>
        ))}
      </fieldset>
      <Field label={f.mode === "perGuest" ? "Amount per guest" : "Total amount"}
        hint={f.mode === "perGuest" ? (guests > 0 ? (amount > 0 ? fmt(amount) + " × " + guests + " guests = " + fmt(amount * guests) : guests + " guests on their profile.") : "Set their guest count on the Clients screen.") : null}>
        <input className="input" inputMode="decimal" value={f.amount} onChange={e => set("amount", e.target.value)} />
      </Field>
    </Dialog>
  );
}

const budgetText = t => t.mode === "perGuest" ? fmt(t.amount) + " × " + (t.guests > 0 ? t.guests + " guests = " + fmt(t.target) : "guests (count not set)") : fmt(t.amount);

export function BudgetView({ client, vendors, expenses, today, onSetBudget, onSaveExpense, onDeleteExpense, onMarkVendorPaid, onOpenVendor,
  fees, settings, onFees, budgets, onBudgets, budgetCategories }) {
  const s = budgetSummary({ client, vendors, expenses, today });
  const [editExp, setEditExp] = useState(null); // null | "new" | expense
  const [editCat, setEditCat] = useState(null); // null | "new" | row
  // Phase 13 · Step 6: her fee, the same records as Quotes & Contracts → Payments.
  const feeList = Array.isArray(fees) ? fees : [];
  const fs = feeSummary(feeList, today, settings);
  const setFee = (id, patch) => onFees(feeList.map(f => f.id === id ? { ...f, ...patch } : f));
  const feeAlert = fs.rows.find(f => f.status === "Overdue");
  // Phase 13 · Step 7: category budgets.
  const nb = normalizeBudgets(budgets);
  const targets = categoryTargets({ budgets: nb, client, vendors, expenses });
  const saveCat = (next, prev) => onBudgets({ ...nb, categories: prev ? nb.categories.map(c => c.category === prev.category ? next : c) : [...nb.categories, next] });
  const delCat = row => onBudgets({ ...nb, categories: nb.categories.filter(c => c.category !== row.category) });
  // One oxblood mark per screen: an overdue fee first, then the first overdue payment, then the first category over budget.
  // The donut's oxblood slice (whole wedding over budget) comes after overdue money and before a single category.
  const alert = feeAlert ? null : s.comingDue.find(x => x.status === "Overdue");
  const donutOver = s.over && !feeAlert && !alert;
  const overAlert = feeAlert || alert || s.over ? null : targets.find(t => t.over);
  return (
    <div className="stack" style={{ "--gap": "24px" }}>
      <div className="grid-4" style={{ "--gap": "16px" }}>
        <Stat label="Total budget" value={s.hasBudget ? fmt(s.budget) : "Not set"} note={s.hasBudget ? "Contingency buffer " + fmt(s.budget * 0.05) : "Add it to track what's left"} />
        <Stat label="Committed" value={fmt(s.committed)} note={s.pct != null ? s.pct + "% of budget" : "Booked vendors and costs"} />
        <Stat label="Paid to date" value={fmt(s.paid)} note="Payments marked paid" />
        <Stat label="Remaining" value={s.remaining != null ? fmt(s.remaining) : "—"} note={s.over ? "Over budget" : "Not yet committed"} />
      </div>
      {s.pct != null && <Meter value={Math.min(100, s.pct)} label="Share of budget committed" />}
      {feeList.length > 0 && <div className="label" style={{ textTransform: "none", letterSpacing: "0.04em" }}>My fee: {fmtCents(fs.paid)} paid of {fmtCents(fs.total)} · not counted in the wedding budget above</div>}
      {onSetBudget && <BudgetTotalEditor value={s.budget} onSave={onSetBudget} />}

      <div className="split" style={{ "--a": "1fr", "--b": "0.9fr" }}>
        <div className="panel">
          <div className="label">Committed by category</div>
          <Donut entries={s.categories} total={s.committed} over={donutOver} />
        </div>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 6 }}>Coming due</div>
          {s.comingDue.length === 0 && <div className="empty">Nothing outstanding.</div>}
          {s.comingDue.slice(0, 4).map((al, i) => (
            <div key={i} className="list-row" style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16.5, fontWeight: 500 }}>{al.label}</div>
                <div className="label" style={{ marginTop: 3 }}>{al.due ? "Due " + prettyDay(al.due) + " · " : ""}{fmtCents(al.amount)}</div>
              </div>
              {/* The first overdue item is this screen's one oxblood mark. */}
              <span className={"tag " + (al.status === "Overdue" ? (al === alert ? "tag-alert" : "tag-outline-ink") : statusClass(al.status))}>{al.status}</span>
            </div>
          ))}
        </div>
      </div>

      {onFees && (
        <section className="panel flush" aria-label="My fee">
          <div className="panel-head">
            <div className="label" style={{ marginRight: "auto" }}>My fee</div>
            {feeList.length > 0 && <span className="mono" style={{ fontSize: 13 }}>Outstanding {fmtCents(fs.outstanding)}</span>}
          </div>
          {feeList.length === 0
            ? <div style={{ padding: "0 22px 18px" }}><Note>No fee payments yet. Your fee schedule shows here once the agreement is in effect (countersigned, or marked signed on paper).</Note></div>
            : <table className="table table-cards">
                <thead><tr><th>Payment</th><th>Due</th><th style={{ textAlign: "right" }}>Amount</th><th>Status</th><th><span className="sr-only">Mark paid</span></th></tr></thead>
                <tbody>
                  {fs.rows.map(f => (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 500 }}>{f.label}</td>
                      <td data-label="Due" className="figure" style={{ fontSize: 13.5 }}>{f.due ? prettyDay(f.due) : "—"}</td>
                      <td data-label="Amount" className="figure" style={{ textAlign: "right" }}>{fmtCents(f.amount)}</td>
                      <td data-label="Status"><span className={"tag " + (f.status === "Overdue" ? (f === feeAlert ? "tag-alert" : "tag-outline-ink") : statusClass(f.status))}>{f.status}</span>{f.paidAt && <div className="field-hint" style={{ marginTop: 4 }}>{prettyDay(String(f.paidAt).slice(0, 10))}</div>}</td>
                      <td data-label="">{f.paidAt
                        ? <button className="btn-link" onClick={() => setFee(f.id, { paidAt: null })} aria-label={"Undo: " + f.label + " paid"}>Undo</button>
                        : <button className="btn btn-secondary" onClick={() => setFee(f.id, { paidAt: today })} aria-label={"Mark paid: " + f.label}>Mark paid</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </section>
      )}

      {onBudgets && (
        <section className="panel flush" aria-label="Category budgets">
          <div className="panel-head">
            <div className="label" style={{ marginRight: "auto" }}>Category budgets</div>
            <button className="btn btn-secondary" onClick={() => setEditCat("new")}>+ Add a category budget</button>
          </div>
          <table className="table table-cards">
            <thead><tr><th>Category</th><th>Budget</th><th style={{ textAlign: "right" }}>Booked</th><th style={{ textAlign: "right" }}>Remaining</th><th>Status</th><th><span className="sr-only">Edit</span></th></tr></thead>
            <tbody>
              {targets.length === 0 && <EmptyRow cols={6}>No category budgets yet. Set a cap for a category (flowers: $5,000) or a per-guest amount (catering: $90 a plate).</EmptyRow>}
              {targets.map(t => (
                <tr key={t.category}>
                  <td style={{ fontWeight: 500 }}>{t.category}</td>
                  <td data-label="Budget" className="figure" style={{ fontSize: 14 }}>{budgetText(t)}</td>
                  <td data-label="Booked" className="figure" style={{ textAlign: "right" }}>{fmtCents(t.booked)}</td>
                  <td data-label="Remaining" className="figure-strong" style={{ textAlign: "right" }}>{t.remaining == null ? "—" : fmtCents(t.remaining)}</td>
                  <td data-label="Status">{t.target == null
                    ? <span className="field-hint">Set their guest count on the Clients screen.</span>
                    : t.over ? <span className={"tag " + (t === overAlert ? "tag-alert" : "tag-outline-ink")}>Over by {fmtCents(t.booked - t.target)}</span>
                    : <StatusTag status="Pending">Within budget</StatusTag>}</td>
                  <td data-label=""><button className="btn btn-icon btn-ghost" aria-label={"Edit budget: " + t.category} onClick={() => setEditCat(nb.categories.find(c => c.category === t.category))}><Icon path={EditIcon} size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "12px 22px 16px" }}>
            <CheckLabel checked={nb.shareWithCouple} onChange={v => onBudgets({ ...nb, shareWithCouple: v })}>Show these budgets in the couple&rsquo;s portal</CheckLabel>
          </div>
        </section>
      )}

      <div className="split" style={{ "--a": "1.4fr", "--b": "1fr" }}>
        <div className="panel flush">
          <div className="panel-head"><div className="label">Vendor payments</div></div>
          <table className="table table-cards">
            <thead><tr><th>Payment</th><th>Due</th><th>Method</th><th style={{ textAlign: "right" }}>Amount</th><th>Status</th>{onMarkVendorPaid && <th><span className="sr-only">Mark paid</span></th>}</tr></thead>
            <tbody>
              {s.payments.length === 0 && <EmptyRow cols={onMarkVendorPaid ? 6 : 5}>No vendor payments yet. Add deposits and balances when you book a vendor (Vendors → open a vendor → Edit).</EmptyRow>}
              {s.payments.map(p => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 500 }}>{onOpenVendor ? <button className="sortbtn" style={{ fontFamily: "var(--font-body)", textTransform: "none", letterSpacing: 0, fontSize: 16, fontWeight: 500 }} onClick={() => onOpenVendor(p.vendorId)}>{p.label}</button> : p.label}</td>
                  <td data-label="Due" className="figure" style={{ fontSize: 13.5 }}>{p.due ? prettyDay(p.due) : "—"}</td>
                  <td data-label="Method">{p.method || "—"}</td>
                  <td data-label="Amount" className="figure" style={{ textAlign: "right" }}>{fmtCents(p.amount)}</td>
                  <td data-label="Status"><span className={"tag " + (p.status === "Overdue" ? "tag-outline-ink" : statusClass(p.status))}>{p.status}</span></td>
                  {onMarkVendorPaid && <td data-label="">{p.paid
                    ? <button className="btn-link" onClick={() => onMarkVendorPaid(p.vendorId, p.kind, false)} aria-label={"Undo: mark " + p.label + " unpaid"}>Undo</button>
                    : <button className="btn btn-secondary" onClick={() => onMarkVendorPaid(p.vendorId, p.kind, true)} aria-label={"Mark paid: " + p.label}>Mark paid</button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="stack" style={{ "--gap": "22px" }}>
          <div className="panel flush">
            <div className="panel-head"><div className="label">Per-vendor totals</div></div>
            <table className="table table-cards">
              <thead><tr><th>Vendor</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>Paid</th><th style={{ textAlign: "right" }}>Balance</th></tr></thead>
              <tbody>
                {s.lineItems.length === 0 && <EmptyRow cols={4}>No booked vendors yet. Mark a vendor Booked on the Vendors screen and it shows here.</EmptyRow>}
                {s.lineItems.map(li => (
                  <tr key={li.id}>
                    <td style={{ fontWeight: 500 }}>{li.name}</td>
                    <td data-label="Total" className="figure" style={{ textAlign: "right" }}>{fmtCents(li.total)}</td>
                    <td data-label="Paid" className="figure" style={{ textAlign: "right" }}>{fmtCents(li.paid)}</td>
                    <td data-label="Balance" className="figure-strong" style={{ textAlign: "right" }}>{fmtCents(li.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel" style={{ gap: 0 }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <div className="label">Other costs</div>
              {onSaveExpense && <button className="btn btn-secondary push" onClick={() => setEditExp("new")}>+ Add a cost</button>}
            </div>
            {s.expenses.length === 0 && <div className="empty">No other costs yet. Attire, rings, favors, tips and the license go here.</div>}
            {s.expenses.map(ex => (
              <div key={ex.id} className="list-row list-split" style={{ fontSize: 15.5, alignItems: "center", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {onSaveExpense ? <button className="sortbtn" style={{ fontFamily: "var(--font-body)", textTransform: "none", letterSpacing: 0, fontSize: 15.5 }} onClick={() => setEditExp((expenses || []).find(e => e.id === ex.id))} aria-label={"Edit cost: " + ex.label}>{ex.label}</button> : ex.label}
                  {ex.due && !ex.paid ? <span className="text-muted" style={{ fontSize: 14 }}> · due {shortDay(ex.due)}</span> : null}
                </span>
                <span className="figure">{fmtCents(ex.amount)}</span>
                {onSaveExpense
                  ? <button className={"chip chip-sm" + (ex.paid ? " active" : "")} aria-pressed={ex.paid} onClick={() => { const e = (expenses || []).find(x => x.id === ex.id); onSaveExpense({ ...e, paid: !e.paid, paidAt: e.paid ? null : new Date().toISOString() }, false); }}>{ex.paid ? "Paid" : "Mark paid"}</button>
                  : <StatusTag status={ex.paid ? "Paid" : "Pending"} />}
              </div>
            ))}
            {s.expenses.length > 0 && <div className="list-split" style={{ paddingTop: 12, borderTop: "1px solid var(--ink)" }}><span className="label" style={{ color: "var(--ink)" }}>Total</span><span className="figure-strong">{fmtCents(s.expTotal)}</span></div>}
          </div>
        </div>
      </div>
      {editExp && <ExpenseDialog expense={editExp === "new" ? null : editExp} onSave={onSaveExpense} onDelete={onDeleteExpense} onClose={() => setEditExp(null)} />}
      {editCat && <CategoryBudgetDialog row={editCat === "new" ? null : editCat} categories={[...new Set([...(budgetCategories || []), ...EXPENSE_CATEGORIES])]}
        taken={new Set(nb.categories.map(c => c.category.toLowerCase()))} guests={Math.max(0, Math.round(Number(client && client.guests) || 0))}
        onSave={saveCat} onDelete={delCat} onClose={() => setEditCat(null)} />}
    </div>
  );
}

/** The couple's Budget tab in their portal: totals, the chart, their other costs, her fee and the category budgets. */
export function CoupleBudget({ client, vendors, expenses, today, fees, budgets }) {
  const s = budgetSummary({ client, vendors, expenses, today });
  const feeRows = Array.isArray(fees) ? fees : [];
  const targets = budgets && budgets.categories ? categoryTargets({ budgets, client, vendors, expenses }) : [];
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <div className="grid-4" style={{ "--gap": "16px" }}>
        <Stat label="Total budget" value={s.hasBudget ? fmt(s.budget) : "Not set yet"} />
        <Stat label="Committed" value={fmt(s.committed)} note={s.pct != null ? s.pct + "% of budget" : null} />
        <Stat label="Paid to date" value={fmt(s.paid)} />
        <Stat label="Remaining" value={s.remaining != null ? fmt(s.remaining) : "—"} />
      </div>
      {s.pct != null && <Meter value={Math.min(100, s.pct)} label="Share of budget committed" />}
      <div className="panel measure" style={{ "--measure": "620px" }}>
        <div className="label">Committed by category</div>
        <Donut entries={s.categories} total={s.committed} over={false} />
      </div>
      {s.expenses.length > 0 && (
        <div className="panel measure" style={{ "--measure": "620px", gap: 0 }}>
          <div className="label" style={{ marginBottom: 6 }}>Other costs</div>
          {s.expenses.map(ex => <div key={ex.id} className="list-row list-split" style={{ fontSize: 15.5 }}><span>{ex.label}</span><span className="figure">{fmtCents(ex.amount)}</span></div>)}
        </div>
      )}
      {targets.length > 0 && (
        <section className="panel flush measure" style={{ "--measure": "620px" }} aria-label="Category budgets">
          <div className="panel-head" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
            <div className="label">Category budgets</div>
            <div className="text-muted" style={{ fontSize: 15.5 }}>What we&rsquo;ve set aside for each part of the day.</div>
          </div>
          <table className="table table-cards">
            <thead><tr><th>Category</th><th style={{ textAlign: "right" }}>Target</th><th style={{ textAlign: "right" }}>Booked</th><th style={{ textAlign: "right" }}>Remaining</th></tr></thead>
            <tbody>
              {targets.map(t => (
                <tr key={t.category}>
                  <td style={{ fontWeight: 500 }}>{t.category}{t.mode === "perGuest" && <div className="text-muted" style={{ fontSize: 14 }}>{fmt(t.amount)} per guest</div>}</td>
                  <td data-label="Target" className="figure" style={{ textAlign: "right" }}>{t.target == null ? "—" : fmt(t.target)}</td>
                  <td data-label="Booked" className="figure" style={{ textAlign: "right" }}>{fmt(t.booked)}</td>
                  <td data-label="Remaining" className="figure" style={{ textAlign: "right" }}>{t.remaining == null ? "—" : fmt(Math.max(0, t.remaining))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {feeRows.length > 0 && (
        <section className="panel measure" style={{ "--measure": "620px", gap: 0 }} aria-label="Your planning fee">
          <div className="label" style={{ marginBottom: 6 }}>Your planning fee</div>
          {feeRows.map((f, i) => (
            <div key={i} className="list-row list-split" style={{ fontSize: 15.5, alignItems: "baseline", gap: 10 }}>
              <span style={{ flex: 1, minWidth: 0 }}>{f.label}</span>
              <span className="figure">{fmtCents(f.amount)}</span>
              <span className={f.paid ? "" : "text-muted"} style={{ minWidth: 150, textAlign: "right" }}>{f.paid ? "Paid, thank you" : f.due ? "Due " + prettyDay(f.due) : "Not due yet"}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

/* ───────────── Timeline (Step 4) ───────────── */

function MilestoneDialog({ milestone, onSave, onDelete, onClose }) {
  const isNew = !milestone;
  const [f, setF] = useState({ date: milestone ? milestone.date || "" : "", title: milestone ? milestone.title : "", desc: milestone ? milestone.desc || "" : "", done: milestone ? !!milestone.done : false });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = () => { if (!f.title.trim()) return; onSave({ ...(milestone || { id: "ms-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5), source: "custom" }), date: f.date, title: f.title.trim(), desc: f.desc.trim(), done: f.done }, isNew); onClose(); };
  return (
    <Dialog title={isNew ? "Add milestone" : "Edit milestone"} kicker="Timeline" onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(milestone); onClose(); }}>Delete</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.title.trim()} onClick={save}>{isNew ? "Add milestone" : "Save"}</button></>}>
      <Field label="Milestone" required><input className="input" value={f.title} onChange={e => set("title", e.target.value)} /></Field>
      <Field label="Date"><input className="input" type="date" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
      <Field label="Details"><textarea className="input" value={f.desc} onChange={e => set("desc", e.target.value)}></textarea></Field>
      <CheckLabel checked={f.done} onChange={v => set("done", v)}>Done</CheckLabel>
    </Dialog>
  );
}

function RunItemDialog({ item, vendors, people, onSave, onDelete, onClose, areas }) {
  const isNew = !item;
  const [f, setF] = useState({ time: item ? timeValue(item.time) : "", end: item ? timeValue(item.end) : "", timeText: item && !timeValue(item.time) ? item.time : "", event: item ? item.event : "", vendor: item ? item.vendor || "" : "",
    zone: item ? item.zone || "" : "", note: item ? item.note || "" : "", person: item ? item.person || "" : "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = () => { if (!f.event.trim()) return; onSave({ ...(item || { id: "rs-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5) }), time: f.time || f.timeText.trim(), end: f.time && f.end && f.end !== f.time ? f.end : "", event: f.event.trim(), vendor: f.vendor.trim(), zone: f.zone.trim(), note: f.note.trim(), person: f.person.trim() }, isNew); onClose(); };
  const vendorNames = [...new Set(["GhostWriter Events", ...vendors.map(v => v.name), f.vendor].filter(Boolean))];
  return (
    <Dialog title={isNew ? "Add to the " + TIMELINE_LABELS.day.toLowerCase() : "Edit " + TIMELINE_LABELS.item} kicker={TIMELINE_LABELS.dayShort} onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(item); onClose(); }}>Delete</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.event.trim()} onClick={save}>{isNew ? "Add item" : "Save"}</button></>}>
      <div className="grid-2">
        <Field label="Start time"><input className="input" type="time" value={f.time} onChange={e => set("time", e.target.value)} /></Field>
        <Field label="End time" hint="Optional."><input className="input" type="time" value={f.end} disabled={!f.time} onChange={e => set("end", e.target.value)} /></Field>
      </div>
      <div className="row" role="group" aria-label="Length" style={{ "--gap": "6px", alignItems: "center" }}>
        <span className="label" style={{ marginRight: 4 }}>Length</span>
        {[15, 30, 45, 60].map(n => (
          <button key={n} type="button" className={"chip chip-sm" + (f.time && f.end && minutesBetween(f.time, f.end) === n ? " active" : "")} aria-pressed={!!(f.time && f.end && minutesBetween(f.time, f.end) === n)}
            disabled={!f.time} onClick={() => set("end", addMinutes(f.time, n))}>{n} min</button>
        ))}
        {f.time && f.end && <span className="field-hint" style={{ marginLeft: 6 }}>{prettyRange(f.time, f.end)} · {durationLabel(minutesBetween(f.time, f.end))}</span>}
        {f.end && <button type="button" className="btn-link" onClick={() => set("end", "")}>Clear end</button>}
      </div>
      {f.time && f.end && f.end < f.time && <div className="field-hint">End time is before the start; I'll assume it's the next day.</div>}
      {!f.time && <div className="field-hint">Pick a start time to use the length buttons.</div>}
      <Field label="Area"><input className="input" list="rs-areas" value={f.zone} onChange={e => set("zone", e.target.value)} /></Field>
      <datalist id="rs-areas">{(areas || []).map((a, i) => <option key={i} value={a.split(":")[0]} />)}</datalist>
      {f.timeText && <div className="field-hint">Saved as &ldquo;{f.timeText}&rdquo;. Pick a time above to replace it.</div>}
      <Field label="What happens" required><input className="input" value={f.event} onChange={e => set("event", e.target.value)} /></Field>
      <Field label="Vendor">
        <select className="input" value={f.vendor} onChange={e => set("vendor", e.target.value)}>
          <option value="">No vendor</option>
          {vendorNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>
      {people && people.length > 0 && (
        <Field label="Person" hint="Someone from their wedding party or important people.">
          <select className="input" value={f.person} onChange={e => set("person", e.target.value)}>
            <option value="">No one in particular</option>
            {people.map(p => <option key={p.id} value={p.name + (p.role ? " (" + p.role + ")" : "")}>{p.name}{p.role ? " · " + p.role : ""}</option>)}
          </select>
        </Field>
      )}
      <Field label="Note"><input className="input" value={f.note} onChange={e => set("note", e.target.value)} /></Field>
    </Dialog>
  );
}

function ListEditor({ title, items, onChange, placeholder, tone }) {
  const [v, setV] = useState("");
  const add = () => { if (!v.trim()) return; onChange([...(items || []), v.trim()]); setV(""); };
  return (
    <div className="panel" style={{ gap: 6, ...(tone ? { background: "var(--vellum)", borderColor: "var(--vellum)" } : {}) }}>
      <div className="label">{title}</div>
      {(items || []).length === 0 && !onChange && <div className="empty">Nothing here yet.</div>}
      {(items || []).map((z, i) => (
        <div key={i} className="list-row" style={{ fontSize: 15.5, display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ flex: 1, minWidth: 0 }}>{z}</span>
          {onChange && <button className="btn btn-icon btn-ghost" aria-label={"Remove: " + z} onClick={() => onChange(items.filter((_, j) => j !== i))}><Icon path={TrashIcon} size={15} /></button>}
        </div>
      ))}
      {onChange && (
        <div className="row" style={{ "--gap": "8px", marginTop: 6 }}>
          <input className="input" style={{ flex: "1 1 180px" }} aria-label={"Add to " + title} placeholder={placeholder} value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") add(); }} />
          <button className="btn btn-secondary" disabled={!v.trim()} onClick={add}>Add</button>
        </div>
      )}
    </div>
  );
}

/**
 * The planning timeline and the wedding day timeline. With the on* handlers it's Amanda's editor; without
 * them it's the couple's read-only view in their portal.
 */
export function TimelineView({ client, milestones, dayOf, vendors, people, settings, today, view, setView, onMilestones, onDayOf }) {
  const ms = sortMilestones(milestones);
  const d = normalizeDayOf(dayOf);
  const runRows = sortRunOfShow(d.items);
  const booked = (vendors || []).filter(v => v.client === client.id && isCommitted(v));
  const [msEdit, setMsEdit] = useState(null);
  const [rsEdit, setRsEdit] = useState(null);
  const [tplNote, setTplNote] = useState("");
  const editable = !!onMilestones;
  const fromTemplate = () => {
    const r = buildMilestones({ client, settings, today });
    if (r.missingDate) { setTplNote("Set their wedding date first (Clients → Edit agreement details)."); return; }
    const have = new Set(ms.map(m => m.title.toLowerCase()));
    const add = r.items.filter(m => !have.has(m.title.toLowerCase()));
    onMilestones([...ms, ...add]);
    setTplNote("Added " + add.length + " milestone" + (add.length === 1 ? "" : "s") + " from the template" + (r.skipped ? "; " + r.skipped + " earlier one" + (r.skipped === 1 ? " was" : "s were") + " left out because the date has passed." : ".") + " Edit or delete any of them.");
  };
  const saveMs = (m, isNew) => onMilestones(isNew ? [...ms, m] : ms.map(x => x.id === m.id ? m : x));
  const delMs = m => onMilestones(ms.filter(x => x.id !== m.id));
  const setDay = patch => onDayOf({ ...d, ...patch });
  const saveRs = (it, isNew) => setDay({ items: isNew ? [...d.items, it] : d.items.map(x => x.id === it.id ? it : x) });
  const delRs = it => setDay({ items: d.items.filter(x => x.id !== it.id) });
  const next = ms.find(m => !m.done && m.date && m.date >= today);

  return (
    <div className="stack" style={{ "--gap": "24px" }}>
      <div className="stack" style={{ "--gap": "10px" }}>
        <div className="tabs" role="tablist" aria-label="Timeline view">
          <button role="tab" className="tab" aria-selected={view === "milestones"} onClick={() => setView("milestones")}>{TIMELINE_LABELS.planning}</button>
          <button role="tab" className="tab" aria-selected={view === "day"} onClick={() => setView("day")}>{TIMELINE_LABELS.day}</button>
        </div>
        <div className="label">{[client.full, client.date, client.venue].filter(Boolean).join(" · ")}</div>
      </div>

      {view === "milestones" && (
        <div className="stack measure" style={{ "--gap": "16px", "--measure": "760px" }}>
          {editable && (
            <div className="row" style={{ "--gap": "10px" }}>
              <button className="btn btn-primary" onClick={() => setMsEdit("new")}>+ Add milestone</button>
              <button className="btn btn-secondary" onClick={fromTemplate}>{ms.length ? "Add any missing from the template" : "Start from the " + ((settings && (settings.packages || []).find(p => p.id === client.packageId) || {}).name || "package") + " template"}</button>
            </div>
          )}
          {tplNote && <div className="field-hint" role="status">{tplNote}</div>}
          {ms.length === 0 && <div className="empty">{editable ? "No milestones yet. Start from the template: it counts back from the wedding date and includes the agreement's deadlines." : "Your planning timeline will show here once I've set it up."}</div>}
          {ms.length > 0 && (
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {ms.map((m, i) => (
                <li key={m.id || i} className="timeline-item">
                  <div className="timeline-mark" aria-hidden="true">
                    <span className={"dot" + (m.done ? " done" : "")}></span>
                    {i < ms.length - 1 && <span className="line"></span>}
                  </div>
                  <div style={{ paddingBottom: 22, minWidth: 0, flex: 1 }}>
                    <div className="row" style={{ "--gap": "4px 14px" }}>
                      <span className="figure text-muted" style={{ fontSize: 13.5 }}>{m.date ? prettyDay(m.date) : "No date"}</span>
                      <span style={{ fontSize: 18, fontWeight: 500 }}>{m.title}</span>
                      <span className="push row" style={{ "--gap": "6px" }}>
                        {m === next && !m.done ? <StatusTag status="Upcoming">Next</StatusTag> : <StatusTag status={m.done ? "Done" : "Upcoming"} />}
                        {editable && <button className={"chip chip-sm" + (m.done ? " active" : "")} aria-pressed={!!m.done} aria-label={(m.done ? "Mark not done: " : "Mark done: ") + m.title} onClick={() => saveMs({ ...m, done: !m.done }, false)}>{m.done ? "Done" : "Mark done"}</button>}
                        {editable && <button className="btn btn-icon btn-ghost" aria-label={"Edit milestone: " + m.title} onClick={() => setMsEdit(m)}><Icon path={EditIcon} size={15} /></button>}
                      </span>
                    </div>
                    {m.desc && <div className="text-muted" style={{ fontSize: 15, marginTop: 4 }}>{m.desc}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {view === "day" && (
        <div className="split" style={{ "--a": "1.7fr", "--b": "1fr" }}>
          <div className="panel flush">
            <div className="panel-head">
              <div className="label">{TIMELINE_LABELS.day}</div>
              {editable && <button className="btn btn-primary push" onClick={() => setRsEdit("new")}>+ Add item</button>}
            </div>
            <table className="table table-cards">
              <thead><tr><th style={{ width: 132 }}>Time</th><th>What happens</th><th>Vendor</th><th>Area</th>{editable && <th><span className="sr-only">Edit</span></th>}</tr></thead>
              <tbody>
                {runRows.length === 0 && <EmptyRow cols={editable ? 5 : 4}>{editable ? "Nothing scheduled yet. Add the day's moments in order: arrivals, ceremony, dinner, send-off." : "Your wedding day timeline will show here once it's drafted."}</EmptyRow>}
                {runRows.map(r => (
                  <tr key={r.id}>
                    <td className="figure-strong" style={{ fontSize: 14.5 }}>{prettyRange(r.time, r.end) || "—"}{r.end && minutesBetween(r.time, r.end) > 0 && <div className="text-muted figure" style={{ fontSize: 13 }}>{durationLabel(minutesBetween(r.time, r.end))}</div>}</td>
                    <td data-label="What happens" className="stacked"><div>{r.event}</div>{(r.person || r.note) && <div className="text-muted" style={{ fontSize: 14.5 }}>{[r.person, r.note].filter(Boolean).join(" · ")}</div>}</td>
                    <td data-label="Vendor" style={{ fontSize: 15 }}>{r.vendor || "—"}</td>
                    <td data-label="Area">{r.zone ? <span className="tag tag-neutral">{r.zone}</span> : "—"}</td>
                    {editable && <td data-label=""><button className="btn btn-icon btn-ghost" aria-label={"Edit " + TIMELINE_LABELS.item + ": " + r.event} onClick={() => setRsEdit(r)}><Icon path={EditIcon} size={15} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="stack" style={{ "--gap": "18px" }}>
            <ListEditor title="Areas & vendor parking" items={d.areas} onChange={editable ? areas => setDay({ areas }) : null} placeholder="e.g. Barn: dinner and dancing" />
            <ListEditor title="Weather backup & contingencies" tone items={d.backups} onChange={editable ? backups => setDay({ backups }) : null} placeholder="e.g. Rain: ceremony moves into the barn, call by noon" />
            {/* Read-only (the portal) shows layouts as they arrive: with a short-lived link, never a storage path. */}
            <VenueLayoutPanel client={client} layouts={editable ? d.layouts : ((dayOf && Array.isArray(dayOf.layouts)) ? dayOf.layouts : [])} areas={d.areas} onChange={editable ? layouts => setDay({ layouts }) : null} />
          </div>
        </div>
      )}
      {msEdit && <MilestoneDialog milestone={msEdit === "new" ? null : msEdit} onSave={saveMs} onDelete={delMs} onClose={() => setMsEdit(null)} />}
      {rsEdit && <RunItemDialog item={rsEdit === "new" ? null : rsEdit} vendors={booked} people={people} areas={d.areas} onSave={saveRs} onDelete={delRs} onClose={() => setRsEdit(null)} />}
    </div>
  );
}

/* ───────────── Venue layouts (Phase 13 · Step 15) ─────────────
   On the wedding-day timeline: floor plans, diagrams, a rain plan. Hosted: the file is in the private bucket under
   <studio>/<couple>/layouts/ and shown through a short-lived link. Browser-only: images as a small copy; PDFs need hosting.
   In the portal a layout arrives as { caption, areas, name, type, isImage, url } (a 10-minute link, no path). */
export function LayoutPreview({ layout, height = 150 }) {
  const portal = "url" in layout;
  const image = portal ? layout.isImage : (layout.dataUrl ? true : isImageFile(layout.file));
  const name = portal ? layout.name : (layout.file && layout.file.name) || "Layout";
  if (image && portal) return layout.url ? <img src={layout.url} alt={layout.caption || name} className="layout-img" style={{ height }} /> : <div className="washed ph-photo" style={{ height }}>{layout.caption || name}</div>;
  if (image) return <div className="layout-img-wrap"><StoredPhoto photo={layout.dataUrl ? { dataUrl: layout.dataUrl } : { path: layout.file.path }} alt={layout.caption || name} height={height} /></div>;
  return (
    <div className="layout-doc" style={{ height }}>
      <span className="label">PDF</span>
      <span className="layout-doc-name">{name}</span>
      {portal ? (layout.url ? <a className="btn btn-secondary" href={layout.url} target="_blank" rel="noopener noreferrer">Open</a> : null) : <DocFileLink file={layout.file} />}
    </div>
  );
}

function LayoutDialog({ client, layout, areas, onSave, onDelete, onClose }) {
  const isNew = !layout;
  const [caption, setCaption] = useState(layout ? layout.caption : "");
  const [chosen, setChosen] = useState(layout ? layout.areas : []);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const st = getActiveStore();
  const cloud = st.mode === "cloud" && st.uploadDocFile;
  const toggle = a => setChosen(c => c.includes(a) ? c.filter(x => x !== a) : [...c, a]);
  const pick = f => {
    setErr(""); setFile(null);
    if (!f) return;
    const problem = checkLayoutFile(f);
    if (problem) { setErr(problem); return; }
    if (!cloud && !/^image\/(jpeg|png)$/i.test(f.type || "")) { setErr("PDF layouts need the hosted app. Here, save it as a JPG or PNG."); return; }
    setFile(f);
    if (!caption) setCaption(f.name.replace(/\.[^.]+$/, ""));
  };
  const save = async () => {
    if (isNew && !file) return;
    setBusy(true); setErr("");
    try {
      let next;
      if (isNew) {
        const id = "lay-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
        if (cloud) next = { id, caption: caption.trim(), areas: chosen, file: await st.uploadDocFile(client.id + "/layouts", file) };
        else {
          // Browser-only copies must stay under the stored size limit; try smaller before giving up (review fix #4).
          let small = await shrinkImage(file, 1600, 0.8);
          if (small.dataUrl.length > 1400000) small = await shrinkImage(file, 1100, 0.6);
          if (small.dataUrl.length > 1400000) throw new Error("That image is too detailed to keep in the browser. Use the hosted app for this layout.");
          next = { id, caption: caption.trim(), areas: chosen, file: null, dataUrl: small.dataUrl };
        }
      } else next = { ...layout, caption: caption.trim(), areas: chosen };
      onSave(next, isNew);
      onClose();
    } catch (e) { setErr(e.message || "That file couldn't be added."); setBusy(false); }
  };
  return (
    <Dialog title={isNew ? "Add a venue layout" : "Edit layout"} kicker={client.names} onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(layout); onClose(); }}>Remove layout</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy || (isNew && !file)} onClick={save}>{busy ? "Adding…" : isNew ? "Add layout" : "Save"}</button></>}>
      {isNew && (
        <Field label="File" hint={cloud ? "A PDF, JPG or PNG, up to 25 MB." : "A JPG or PNG (PDFs need the hosted app)."}>
          <input className="input" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={e => pick(e.target.files && e.target.files[0])} />
        </Field>
      )}
      <Field label="Caption" hint="e.g. Reception, rain plan"><input className="input" value={caption} onChange={e => setCaption(e.target.value)} /></Field>
      {areas.length > 0 && (
        <fieldset className="stack" style={{ "--gap": "8px", border: 0, padding: 0, margin: 0 }}>
          <legend className="field-label">Areas it shows (optional)</legend>
          <div className="row" style={{ "--gap": "6px" }}>
            {areas.map(a => <button key={a} type="button" className={"chip chip-sm" + (chosen.includes(a) ? " active" : "")} aria-pressed={chosen.includes(a)} onClick={() => toggle(a)}>{a}</button>)}
          </div>
        </fieldset>
      )}
      {err && <Note tone="warn">{err}</Note>}
    </Dialog>
  );
}

export function VenueLayoutPanel({ client, layouts, areas, onChange }) {
  const [edit, setEdit] = useState(null); // "new" | layout
  const list = layouts || [];
  const editable = !!onChange;
  if (!editable && !list.length) return null;
  const st = getActiveStore();
  const save = (l, isNew) => onChange(isNew ? [...list, l] : list.map(x => x.id === l.id ? l : x));
  const remove = l => { if (l.file && l.file.path && st.deleteDocFile) st.deleteDocFile(l.file.path); onChange(list.filter(x => x.id !== l.id)); };
  return (
    <section className="panel" style={{ gap: 10 }} aria-label="Venue layout">
      <div className="row" style={{ "--gap": "8px" }}>
        <div className="label">Venue layout</div>
        {editable && list.length < LAYOUTS_MAX && <button className="btn btn-secondary push" onClick={() => setEdit("new")}>+ Add a layout</button>}
      </div>
      <div className="field-hint">{editable ? "Floor plans, diagrams, a rain plan. The couple can see these in their portal." : "The layouts for your day."}</div>
      {list.length === 0 && <div className="empty">No layouts yet.</div>}
      <div className="layout-grid">
        {list.map(l => (
          <figure key={l.id} className="layout-tile">
            <LayoutPreview layout={l} />
            <figcaption className="row" style={{ "--gap": "4px 8px" }}>
              <span className="layout-caption">{l.caption || (l.file && l.file.name) || l.name || "Layout"}</span>
              {editable && <button className="btn btn-icon btn-ghost push" aria-label={"Edit layout: " + (l.caption || "layout")} onClick={() => setEdit(l)}><Icon path={EditIcon} size={15} /></button>}
              {(l.areas || []).length > 0 && <span className="row" style={{ "--gap": "4px", width: "100%" }}>{l.areas.map(a => <span key={a} className="tag tag-neutral">{a}</span>)}</span>}
            </figcaption>
          </figure>
        ))}
      </div>
      {edit && <LayoutDialog client={client} layout={edit === "new" ? null : edit} areas={areas || []} onSave={save} onDelete={remove} onClose={() => setEdit(null)} />}
    </section>
  );
}

/* ───────────── Inspiration board (Phase 13 · Step 14) ─────────────
   One board per couple, shared by her dashboard and their portal. Photo pins: a short-lived `url` in the portal, a
   stored `file` (or a small `dataUrl` in browser-only mode) on the dashboard. Vendors never see it. */
function PinImage({ pin }) {
  const [broken, setBroken] = useState(false);
  const label = pin.note || pin.name || (pin.file && pin.file.name) || "Photo";
  const portal = !pin.file && !pin.dataUrl;
  // Link pins show the site's name, not the linked picture: the app's Content-Security-Policy only allows images from
  // the app and its own storage (open decision #8's fallback; showing linked images inline needs img-src https:).
  const showable = pin.kind === "link" ? false : portal ? pin.isImage !== false : (pin.dataUrl || isImageFile(pin.file));
  if (pin.kind === "photo" && !portal && showable) return <StoredPhoto photo={pin.dataUrl ? { dataUrl: pin.dataUrl } : { path: pin.file.path }} alt={label} height={180} />;
  if (showable && pin.url && !broken) return <img className="pin-img" src={pin.url} alt={label} loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  if (pin.kind === "link") return <div className="pin-site"><span className="label">{pin.isImage ? "Picture link" : "Link"}</span><span className="pin-site-name">{pin.siteName}</span></div>;
  // HEIC and the like: the browser can't draw it, so offer the file itself.
  return (
    <div className="pin-site">
      <span className="label">Photo</span>
      {portal ? (pin.url ? <a className="btn btn-secondary" href={pin.url} target="_blank" rel="noopener noreferrer">Open photo</a> : null) : <DocFileLink file={pin.file} />}
    </div>
  );
}

function PinLinkDialog({ onSave, onClose, categories }) {
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("");
  const [err, setErr] = useState("");
  const save = () => {
    try { normalizeLink(url); } catch (e) { setErr(e.message); return; }
    onSave({ url: url.trim(), note: note.trim(), category });
    onClose();
  };
  return (
    <Dialog title="Add a link" kicker="Inspiration board" onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!url.trim()} onClick={save}>Add link</button></>}>
      <Field label="Link" required hint="A Pinterest pin, a dress, a cake: anything starting with https://"><input className="input" type="url" inputMode="url" value={url} onChange={e => { setUrl(e.target.value); setErr(""); }} /></Field>
      <Field label="What you like about it (optional)"><input className="input" value={note} onChange={e => setNote(e.target.value)} /></Field>
      <Field label="Category">
        <select className="input" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">No category</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      {err && <Note tone="warn">{err}</Note>}
    </Dialog>
  );
}

function PinEditDialog({ pin, categories, onSave, onClose }) {
  const [note, setNote] = useState(pin.note || "");
  const [category, setCategory] = useState(pin.category || "");
  return (
    <Dialog title="Edit pin" kicker="Inspiration board" onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={() => { onSave({ ...pin, note: note.trim(), category }); onClose(); }}>Save</button></>}>
      <Field label="Note"><input className="input" value={note} onChange={e => setNote(e.target.value)} /></Field>
      <Field label="Category">
        <select className="input" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">No category</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
    </Dialog>
  );
}

/**
 * by "planner": she can tag, star, edit and remove any pin. by "couple": they add photos and links and remove their own.
 * onAddPhotos(files, { category, note }) and onAddLink({ url, note, category }) do the saving; busyNote shows progress.
 */
export function InspirationBoard({ pins, by, onAddPhotos, onAddLink, onUpdate, onRemove, busyNote, intro, categories = INSPIRATION_CATEGORIES }) {
  const [filter, setFilter] = useState("All");
  const [linkOpen, setLinkOpen] = useState(false);
  const [editPin, setEditPin] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const planner = by === "planner";
  const list = pins || [];
  const used = categories.filter(c => list.some(p => p.category === c));
  const shown = list.filter(p => filter === "All" || (filter === "Favorites" ? p.favorite : p.category === filter));
  const full = list.length >= PINS_MAX;
  const whoLabel = p => planner ? (p.addedBy === "couple" ? "From the couple" : "Added by me") : (p.addedBy === "couple" ? "Added by you" : "From your planner");
  const readOnly = !onAddPhotos;
  const canRemove = p => !readOnly && (planner || p.addedBy === "couple");
  return (
    <div className="stack" style={{ "--gap": "18px" }}>
      {intro && <p className="text-muted measure" style={{ margin: 0, fontSize: 16, lineHeight: 1.55, "--measure": "760px" }}>{intro}</p>}
      {!readOnly && <div className="row" style={{ "--gap": "10px" }}>
        <label className={"btn btn-primary" + (full ? " is-disabled" : "")} aria-disabled={full || undefined}>
          + Add photos
          <input key={inputKey} type="file" className="sr-only" multiple disabled={full} accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={e => { const fs = Array.from(e.target.files || []); setInputKey(k => k + 1); if (fs.length) onAddPhotos(fs, { category: filter !== "All" && filter !== "Favorites" ? filter : "" }); }} />
        </label>
        <button className="btn btn-secondary" disabled={full} onClick={() => setLinkOpen(true)}>+ Add a link</button>
        <span className="field-hint">JPG, PNG or HEIC, up to 20 MB.</span>
      </div>}
      {full && <Note tone="warn">The board is full ({PINS_MAX} pins). Remove a few to add more.</Note>}
      {busyNote}
      {list.length > 0 && (
        <div role="group" aria-label="Show pins" className="row" style={{ "--gap": "6px" }}>
          {["All", "Favorites", ...used].map(c => (
            <button key={c} className={"chip chip-sm" + (filter === c ? " active" : "")} aria-pressed={filter === c} onClick={() => setFilter(c)}>
              {c === "Favorites" ? "★ Favorites" : c} <span className="chip-count">{c === "All" ? list.length : c === "Favorites" ? list.filter(p => p.favorite).length : list.filter(p => p.category === c).length}</span>
            </button>
          ))}
        </div>
      )}
      {list.length === 0 && <div className="empty">Nothing pinned yet. Start with a photo or a Pinterest link.</div>}
      {list.length > 0 && shown.length === 0 && <div className="empty">Nothing under this filter yet.</div>}
      <ul className="pin-grid" aria-label="Pins">
        {shown.map(p => (
          <li key={p.id} className="pin">
            {p.kind === "link" ? <SafeLink href={p.url}><PinImage pin={p} /></SafeLink> : <PinImage pin={p} />}
            <div className="pin-body">
              <div className="row" style={{ "--gap": "4px 8px" }}>
                {p.favorite && (!planner || readOnly) && <span className="pin-star" aria-label="Favorite" title="Favorite">★</span>}
                {p.category && <span className="tag tag-neutral">{p.category}</span>}
                <span className="label push">{whoLabel(p)}</span>
              </div>
              {p.note && <div style={{ fontSize: 15, overflowWrap: "anywhere" }}>{p.note}</div>}
              {p.kind === "link" && <div className="text-muted" style={{ fontSize: 14 }}><SafeLink href={p.url}>{p.siteName}</SafeLink></div>}
              {((planner && !readOnly) || canRemove(p)) && (
                <div className="row icon-btn-row" style={{ "--gap": "2px" }}>
                  {planner && !readOnly && <button className={"btn btn-icon btn-ghost pin-fav" + (p.favorite ? " on" : "")} aria-pressed={!!p.favorite} aria-label={"Favorite: " + (p.note || p.siteName || "pin")} onClick={() => onUpdate({ ...p, favorite: !p.favorite })}>{p.favorite ? "★" : "☆"}</button>}
                  {planner && !readOnly && <button className="btn btn-icon btn-ghost" aria-label={"Edit pin: " + (p.note || p.siteName || "pin")} onClick={() => setEditPin(p)}><Icon path={EditIcon} size={15} /></button>}
                  {canRemove(p) && <button className="btn btn-icon btn-ghost" aria-label={"Remove pin: " + (p.note || p.siteName || "pin")} onClick={() => onRemove(p)}><Icon path={TrashIcon} size={15} /></button>}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      {linkOpen && <PinLinkDialog categories={categories} onSave={onAddLink} onClose={() => setLinkOpen(false)} />}
      {editPin && <PinEditDialog pin={editPin} categories={categories} onSave={onUpdate} onClose={() => setEditPin(null)} />}
    </div>
  );
}

/* ───────────── AI document drop: the review form (Phase 13 · Step 16) ─────────────
   Nothing saves until she presses Save. Low-confidence or empty fields are marked "Check this"; when the document
   disagrees with a vendor she already has, she picks which to keep. */
const EXTRACT_TO_FORM = [
  ["vendorName", "name", "Vendor / business name"], ["category", "category", "Category"], ["contactName", "contact", "Contact person"],
  ["phone", "phone", "Phone"], ["email", "email", "Email"], ["website", "website", "Website"], ["address", "address", "Address"],
  ["total", "quote", "Total"], ["deposit", "deposit", "Deposit"], ["depositDue", "depositDue", "Deposit due"],
  ["balance", "final", "Balance"], ["balanceDue", "finalDue", "Balance due"], ["paymentMethod", "method", "How they're paid"],
  ["keyTerms", "keyTerms", "Key terms"], ["services", "services", "Services"]
];
const formVal = (k, v) => v == null ? "" : Array.isArray(v) ? v.join(", ") : String(v);

export function ExtractReviewDialog({ clientId, result, existing, vendors, categories, fileName, onSave, onCancel }) {
  const f = result.fields || {};
  const conf = result.confidence || {};
  // Worked out once when the dialog opens, so the new vendor's id doesn't change while she edits.
  const [{ match, base }] = useState(() => {
    const m = existing || (vendors || []).find(v => f.vendorName && v.name.trim().toLowerCase() === f.vendorName.trim().toLowerCase()) || null;
    return { match: m, base: m || blankVendor(clientId, { category: f.category || categories[0] }) };
  });
  const mine = toForm(base);
  const fromDoc = Object.fromEntries(EXTRACT_TO_FORM.map(([src, key]) => [key, formVal(key, f[src])]));
  const conflicts = match ? EXTRACT_TO_FORM.filter(([, key]) => fromDoc[key] && mine[key] && fromDoc[key].trim() !== String(mine[key]).trim()).map(([, key, label]) => ({ key, label })) : [];
  const initial = { ...mine };
  EXTRACT_TO_FORM.forEach(([, key]) => { if (fromDoc[key] && (!match || !mine[key])) initial[key] = fromDoc[key]; });
  const [form, setForm] = useState(initial);
  const [choice, setChoice] = useState(Object.fromEntries(conflicts.map(c => [c.key, "mine"])));
  const [docType, setDocType] = useState(f.docType || "Contract");
  const [cancelTerms, setCancelTerms] = useState(f.cancellationTerms || "");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pickConflict = (key, which) => { setChoice(c => ({ ...c, [key]: which })); set(key, which === "doc" ? fromDoc[key] : String(mine[key])); };
  const check = src => conf[src] !== "high" ? <span className="tag tag-outline check-this">Check this</span> : null;
  const fieldFor = (src, key, label, type) => {
    const conflict = conflicts.find(c => c.key === key);
    return (
      <div key={key} className="stack" style={{ "--gap": "6px" }}>
        <Field label={label}>
          {key === "category"
            ? <select className="input" value={form.category} onChange={e => set("category", e.target.value)}>{[...new Set([form.category, ...categories].filter(Boolean))].map(c => <option key={c}>{c}</option>)}</select>
            : key === "keyTerms" ? <textarea className="input" value={form.keyTerms} onChange={e => set("keyTerms", e.target.value)}></textarea>
            : <input className="input" type={type || "text"} inputMode={["quote", "deposit", "final"].includes(key) ? "decimal" : undefined} value={form[key]} onChange={e => set(key, e.target.value)} />}
        </Field>
        <div className="row" style={{ "--gap": "6px" }}>
          {check(src)}
          {conflict && (
            <span className="conflict" role="group" aria-label={"Which " + label.toLowerCase() + " to keep"}>
              <span className="field-hint">Document says {fromDoc[key]} · you have {String(mine[key])}</span>
              <button type="button" className={"chip chip-sm" + (choice[key] === "doc" ? " active" : "")} aria-pressed={choice[key] === "doc"} onClick={() => pickConflict(key, "doc")}>Use the document&rsquo;s</button>
              <button type="button" className={"chip chip-sm" + (choice[key] === "mine" ? " active" : "")} aria-pressed={choice[key] === "mine"} onClick={() => pickConflict(key, "mine")}>Keep mine</button>
            </span>
          )}
        </div>
      </div>
    );
  };
  const save = () => {
    if (!form.name.trim()) return;
    const vendor = applyVendorForm(base, form);
    const amount = moneyIn(form.quote);
    onSave({ vendor, isNew: !match, doc: { name: (fileName || "Vendor document").replace(/\.[^.]+$/, ""), category: EXTRACT_DOC_TYPES.includes(docType) ? docType : "Other",
      vendor: vendor.name, status: "Received", range: "", amount, terms: form.keyTerms.trim(), cancel: cancelTerms.trim(), insurance: "", file: result.file || null } });
  };
  return (
    <Dialog title="Review what the AI read" kicker={fileName || "Vendor document"} width={760} onClose={onCancel}
      footer={<><button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" disabled={!form.name.trim()} onClick={save}>{match ? "Update vendor and file document" : "Save vendor and file document"}</button></>}>
      <Note>I read this from the document. Check the amounts and dates before saving; nothing is saved until you do.</Note>
      {match && <div className="field-hint">This matches {match.name}, a vendor you already have. Blank fields are filled in from the document; where they differ, pick which to keep.</div>}
      {(result.warnings || []).length > 0 && <Note tone="warn"><strong style={{ fontWeight: 500 }}>Worth a look:</strong> {result.warnings.join(" ")}</Note>}
      <div className="grid-2">
        <Field label="Document type">
          <select className="input" value={docType} onChange={e => setDocType(e.target.value)}>{EXTRACT_DOC_TYPES.map(t => <option key={t}>{t}</option>)}</select>
        </Field>
        {fieldFor("vendorName", "name", "Vendor / business name")}
        {fieldFor("category", "category", "Category")}
        {fieldFor("contactName", "contact", "Contact person")}
        {fieldFor("phone", "phone", "Phone", "tel")}
        {fieldFor("email", "email", "Email", "email")}
        {fieldFor("website", "website", "Website")}
        {fieldFor("total", "quote", "Total")}
        {fieldFor("deposit", "deposit", "Deposit")}
        {fieldFor("depositDue", "depositDue", "Deposit due", "date")}
        {fieldFor("balance", "final", "Balance")}
        {fieldFor("balanceDue", "finalDue", "Balance due", "date")}
        {fieldFor("paymentMethod", "method", "How they're paid")}
      </div>
      {fieldFor("address", "address", "Address")}
      {fieldFor("services", "services", "Services")}
      {fieldFor("keyTerms", "keyTerms", "Key terms")}
      <Field label="Cancellation terms (goes on the document)"><textarea className="input" value={cancelTerms} onChange={e => setCancelTerms(e.target.value)}></textarea></Field>
      {conf.cancellationTerms !== "high" && <div>{check("cancellationTerms")}</div>}
      {result.raw && (
        <details className="disclosure">
          <summary>What the AI wrote back</summary>
          <pre className="raw-ai">{result.raw}</pre>
        </details>
      )}
    </Dialog>
  );
}

/* ───────────── Photos (Past Weddings) ─────────────
   Hosted: the private documents bucket, shown through a short-lived link. Browser-only mode: a small copy
   kept with the data (resized first, so the saved data stays small). */
export function StoredPhoto({ photo, alt, height }) {
  const [src, setSrc] = useState(photo && photo.dataUrl ? photo.dataUrl : null);
  useEffect(() => {
    let live = true;
    if (photo && photo.dataUrl) { setSrc(photo.dataUrl); return; }
    if (!photo || !photo.path) { setSrc(null); return; }
    const st = getActiveStore();
    if (st.docFileUrl) st.docFileUrl(photo.path).then(u => { if (live) setSrc(u); }).catch(() => { if (live) setSrc(null); });
    return () => { live = false; };
  }, [photo && (photo.path || photo.dataUrl)]);
  if (!src) return <div className="washed ph-photo" style={{ height }}>{alt}</div>;
  return <img src={src} alt={alt} style={{ width: "100%", height, objectFit: "cover", display: "block" }} />;
}

export async function shrinkImage(file, max = 1400, quality = 0.82) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("That photo couldn't be read. Try a JPG or PNG.")); i.src = url; });
    const k = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise(res => c.toBlob(res, "image/jpeg", quality));
    return { blob, width: c.width, height: c.height, dataUrl: c.toDataURL("image/jpeg", Math.min(quality, 0.7)) };
  } finally { URL.revokeObjectURL(url); }
}

/* ───────────── Past Weddings (Step 7): her own weddings, with photos ───────────── */

const WEDDING_STYLES = ["Rustic", "Modern", "Coastal", "Garden", "Classic", "Boho", "Vineyard", "Other"];

function WeddingDialog({ wedding, onSave, onDelete, onClose }) {
  const isNew = !wedding;
  const w = wedding || {};
  const [f, setF] = useState({ couple: w.couple || "", date: w.date || "", venue: w.venue || "", style: w.style || "Rustic",
    elements: w.elements || "", credits: (w.credits || []).join(", "), quote: w.quote || "", colors: w.colors || [], photos: w.photos || [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const st = getActiveStore();
  const addPhotos = async files => {
    setErr(""); setBusy(true);
    try {
      const added = [];
      for (const file of Array.from(files || []).slice(0, 12)) {
        if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type || "") && !/\.(jpe?g|png|webp)$/i.test(file.name)) { setErr("Photos only: JPG, PNG or WebP."); continue; }
        const id = "ph-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
        if (st.mode === "cloud" && st.uploadDocFile) {
          const small = await shrinkImage(file, 1800, 0.85);
          const up = await st.uploadDocFile("_portfolio", new File([small.blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
          added.push({ id, path: up.path, name: file.name });
        } else {
          const small = await shrinkImage(file, 900, 0.6);
          added.push({ id, dataUrl: small.dataUrl, name: file.name });
        }
      }
      setF(p => ({ ...p, photos: [...p.photos, ...added] }));
    } catch (e) { setErr(e.message || "That photo couldn't be added."); }
    finally { setBusy(false); }
  };
  const removePhoto = ph => { if (ph.path && st.deleteDocFile) st.deleteDocFile(ph.path); setF(p => ({ ...p, photos: p.photos.filter(x => x.id !== ph.id) })); };
  const save = () => {
    if (!f.couple.trim()) return;
    onSave({ ...(wedding || { id: "w-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5) }), couple: f.couple.trim(), date: f.date.trim(), venue: f.venue.trim(), style: f.style,
      elements: f.elements.trim(), credits: f.credits.split(",").map(x => x.trim()).filter(Boolean), quote: f.quote.trim() || null, colors: f.colors, photos: f.photos }, isNew);
    onClose();
  };
  return (
    <Dialog title={isNew ? "Add a past wedding" : "Edit " + w.couple} kicker="Past Weddings" width={720} onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { (w.photos || []).forEach(ph => { if (ph.path && st.deleteDocFile) st.deleteDocFile(ph.path); }); onDelete(w); onClose(); }}>Delete wedding</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.couple.trim() || busy} onClick={save}>{isNew ? "Add wedding" : "Save"}</button></>}>
      <div className="grid-2">
        <Field label="Couple" required><input className="input" value={f.couple} placeholder="e.g. Ava & Theo" onChange={e => set("couple", e.target.value)} /></Field>
        <Field label="When"><input className="input" value={f.date} placeholder="e.g. Sep 2025" onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Venue"><input className="input" value={f.venue} onChange={e => set("venue", e.target.value)} /></Field>
        <Field label="Style"><select className="input" value={f.style} onChange={e => set("style", e.target.value)}>{WEDDING_STYLES.map(x => <option key={x}>{x}</option>)}</select></Field>
      </div>
      <Field label="What made it theirs"><textarea className="input" value={f.elements} onChange={e => set("elements", e.target.value)}></textarea></Field>
      <Field label="Vendor credits" hint="Comma separated."><input className="input" value={f.credits} onChange={e => set("credits", e.target.value)} /></Field>
      <Field label="What they said (optional)"><input className="input" value={f.quote} onChange={e => set("quote", e.target.value)} /></Field>
      <Field label="Photos" hint="JPG, PNG or WebP. They're resized before saving.">
        <div className="stack" style={{ "--gap": "10px" }}>
          {f.photos.length > 0 && (
            <div className="photo-grid">
              {f.photos.map((ph, i) => (
                <figure key={ph.id}>
                  <StoredPhoto photo={ph} alt={"Photo " + (i + 1) + " of " + (f.couple || "this wedding")} height={96} />
                  <button type="button" className="btn btn-secondary" style={{ width: "100%", marginTop: 4 }} onClick={() => removePhoto(ph)} aria-label={"Remove photo " + (i + 1)}>Remove</button>
                </figure>
              ))}
            </div>
          )}
          <label className="btn btn-secondary" style={{ alignSelf: "flex-start" }}>
            {busy ? "Adding…" : "Add photos"}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} disabled={busy} onChange={e => { addPhotos(e.target.files); e.target.value = ""; }} />
          </label>
          {err && <div className="field-hint" role="alert" style={{ color: "var(--oxblood)" }}>{err}</div>}
        </div>
      </Field>
    </Dialog>
  );
}

export function PastWeddings({ weddings, filter, setFilter, onSave, onDelete }) {
  const list = weddings || [];
  const styles = ["All", ...new Set(list.map(w => w.style).filter(Boolean))];
  const items = list.filter(w => filter === "All" || w.style === filter);
  const [edit, setEdit] = useState(null);
  const [copied, setCopied] = useState("");
  const caption = w => [w.couple + (w.date ? " · " + w.date : "") + (w.venue ? " · " + w.venue : ""), w.elements, w.credits && w.credits.length ? "With " + w.credits.join(", ") : ""].filter(Boolean).join("\n");
  const copy = async w => {
    try { await navigator.clipboard.writeText(caption(w)); setCopied(w.id); setTimeout(() => setCopied(""), 2500); } catch (e) { setCopied(""); }
  };
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <div className="row" style={{ "--gap": "10px" }}>
        {list.length > 0 && (
          <div className="row" role="group" aria-label="Filter by style" style={{ "--gap": "6px" }}>
            {styles.map(st => <button key={st} className={"chip chip-sm" + (filter === st ? " active" : "")} aria-pressed={filter === st} onClick={() => setFilter(st)}>{st}</button>)}
          </div>
        )}
        {onSave && <button className="btn btn-primary push" onClick={() => setEdit("new")}>+ Add a wedding</button>}
      </div>
      {list.length === 0 && <div className="panel measure" style={{ "--measure": "640px" }}><div className="empty">No past weddings yet. Add the ones you&rsquo;re proud of, with a few photos and the vendors who made them.</div></div>}
      <div className="masonry">
        {items.map(w => {
          const ph = (w.photos || [])[0];
          return (
            <article key={w.id} className="panel" style={{ breakInside: "avoid", marginBottom: 20, padding: 0, gap: 0 }}>
              <StoredPhoto photo={ph} alt={w.couple} height={240} />
              <div className="stack" style={{ "--gap": "10px", padding: "18px 20px 16px" }}>
                <div className="row" style={{ "--gap": "6px 10px", alignItems: "baseline" }}>
                  <h3 className="panel-title" style={{ fontSize: 26 }}>{w.couple}</h3>
                  {w.style && <span className="tag tag-neutral push">{w.style}</span>}
                </div>
                {(w.date || w.venue) && <div className="label">{[w.date, w.venue].filter(Boolean).join(" · ")}</div>}
                {(w.colors || []).length > 0 && <div className="row" style={{ "--gap": "6px" }}>{w.colors.map((sw, j) => <span key={j} className="swatch sm" style={{ background: sw }}></span>)}</div>}
                {w.elements && <div style={{ fontSize: 15.5 }}>{w.elements}</div>}
                {(w.photos || []).length > 1 && (
                  <div className="photo-grid">{w.photos.slice(1, 5).map((p, i) => <StoredPhoto key={p.id} photo={p} alt={w.couple + ", photo " + (i + 2)} height={84} />)}</div>
                )}
                {w.quote && <blockquote className="display-italic" style={{ margin: 0, borderLeft: "2px solid var(--ink)", paddingLeft: 14, fontSize: 19, lineHeight: 1.35 }}>&ldquo;{w.quote}&rdquo;</blockquote>}
                {(w.credits || []).length > 0 && <div className="text-muted" style={{ fontSize: 14.5 }}>With {w.credits.join(", ")}</div>}
                <div className="row" style={{ "--gap": "8px" }}>
                  <button className="btn btn-secondary" onClick={() => copy(w)}>{copied === w.id ? "Copied" : "Copy caption"}</button>
                  {onSave && <button className="btn btn-secondary" onClick={() => setEdit(w)}>Edit</button>}
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {edit && <WeddingDialog wedding={edit === "new" ? null : edit} onSave={onSave} onDelete={onDelete} onClose={() => setEdit(null)} />}
    </div>
  );
}

/* ───────────── Bridal Party & Important People (Batch C, Step 12) ───────────── */

function PersonDialog({ person, partners, onSave, onDelete, onClose, by }) {
  const isNew = !person;
  const p = person || {};
  const sideLabel = s => s === "Partner 1's side" && partners[0] ? partners[0] + "'s side" : s === "Partner 2's side" && partners[1] ? partners[1] + "'s side" : s;
  const [f, setF] = useState({ name: p.name || "", role: p.role || "Bridesmaid", side: p.side || "", phone: p.phone || "", email: p.email || "", notes: p.notes || "" });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const save = () => { if (!f.name.trim()) return; onSave({ ...(person || { id: "pp-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5), addedBy: by }), ...f, name: f.name.trim() }, isNew); onClose(); };
  return (
    <Dialog title={isNew ? "Add a person" : "Edit " + p.name} kicker="Wedding party & important people" onClose={onClose}
      footer={<>{!isNew && <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={() => { onDelete(person); onClose(); }}>Remove</button>}
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.name.trim()} onClick={save}>{isNew ? "Add person" : "Save"}</button></>}>
      <div className="grid-2">
        <Field label="Name" required><input className="input" value={f.name} onChange={e => set("name", e.target.value)} /></Field>
        <Field label="Role"><select className="input" value={f.role} onChange={e => set("role", e.target.value)}>{PEOPLE_ROLES.map(r => <option key={r}>{r}</option>)}</select></Field>
        <Field label="Whose side"><select className="input" value={f.side} onChange={e => set("side", e.target.value)}><option value="">Not set</option>{PEOPLE_SIDES.map(s => <option key={s} value={s}>{sideLabel(s)}</option>)}</select></Field>
        <Field label="Phone"><input className="input" type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} /></Field>
      </div>
      <Field label="Email"><input className="input" type="email" value={f.email} onChange={e => set("email", e.target.value)} /></Field>
      <Field label="Notes" hint="A speech, mobility needs, special seating…"><textarea className="input" value={f.notes} onChange={e => set("notes", e.target.value)}></textarea></Field>
    </Dialog>
  );
}

/** The list, for Amanda (People tab) and for the couple (portal → Your people). `by` marks who added someone. */
export function PeopleEditor({ client, people, onChange, by, intro, onContactSheet, busyNote }) {
  const list = sortPeople(people || []);
  const partners = (client.partners || []).map(p => (p && p.name ? p.name.split(" ")[0] : ""));
  const sideLabel = s => s === "Partner 1's side" && partners[0] ? partners[0] + "'s side" : s === "Partner 2's side" && partners[1] ? partners[1] + "'s side" : s;
  const [edit, setEdit] = useState(null);
  const save = (person, isNew) => onChange(isNew ? [...(people || []), person] : (people || []).map(x => x.id === person.id ? person : x));
  const del = person => onChange((people || []).filter(x => x.id !== person.id));
  return (
    <div className="stack" style={{ "--gap": "18px" }}>
      <div className="row" style={{ "--gap": "10px" }}>
        {intro && <p className="module-intro" style={{ flex: "1 1 420px", margin: 0 }}>{intro}</p>}
        {onChange && <button className="btn btn-primary push" onClick={() => setEdit("new")}>+ Add a person</button>}
        {onContactSheet && <button className="btn btn-secondary" onClick={onContactSheet}>Day-of contact sheet</button>}
      </div>
      {busyNote}
      <div className="panel flush">
        <table className="table table-cards">
          <thead><tr><th>Name</th><th>Role</th><th>Side</th><th>Phone</th><th>Notes</th>{onChange && <th><span className="sr-only">Edit</span></th>}</tr></thead>
          <tbody>
            {list.length === 0 && <EmptyRow cols={onChange ? 6 : 5}>{by === "couple" ? "No one here yet. Add your wedding party, parents, officiant and anyone I should know on the day." : "No one here yet. Add the wedding party and important people, or let the couple add them from their portal."}</EmptyRow>}
            {list.map(p => (
              <tr key={p.id}>
                <td><div style={{ fontWeight: 500, fontSize: 17 }}>{p.name}</div>{p.email && <div className="text-muted" style={{ fontSize: 14.5, overflowWrap: "anywhere" }}>{p.email}</div>}</td>
                <td data-label="Role">{p.role}</td>
                <td data-label="Side">{sideLabel(p.side) || "—"}</td>
                <td data-label="Phone" className="figure">{p.phone || "—"}</td>
                <td data-label="Notes" style={{ fontSize: 15 }}>{p.notes || "—"}</td>
                {onChange && <td data-label="">{by === "couple" && p.addedBy !== "couple" ? <span className="field-hint">Added by me</span> : <button className="btn btn-icon btn-ghost" aria-label={"Edit person: " + p.name} onClick={() => setEdit(p)}><Icon path={EditIcon} size={15} /></button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <PersonDialog person={edit === "new" ? null : edit} partners={partners} by={by} onSave={save} onDelete={del} onClose={() => setEdit(null)} />}
    </div>
  );
}

/** A one-page sheet for the wedding day: the couple, the people, the vendors, the wedding day timeline. Prints cleanly. */
export function ContactSheet({ client, people, vendors, dayOf, studioName, plannerName, plannerPhone, onClose }) {
  const ps = sortPeople(people || []);
  const vs = (vendors || []).filter(v => v.client === client.id && isCommitted(v));
  const run = sortRunOfShow(normalizeDayOf(dayOf).items);
  const layouts = normalizeLayouts(dayOf && dayOf.layouts);
  return (
    <Dialog title="Day-of contact sheet" kicker={client.names} width={860} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Close</button><button className="btn btn-primary" onClick={() => window.print()}>Print</button></>}>
      <div className="print-sheet stack" style={{ "--gap": "16px" }} tabIndex={0} role="region" aria-label="Contact sheet">
        <div>
          <div className="label">{[client.date, client.venue, formatTime(client.ceremonyTime) ? "Ceremony " + formatTime(client.ceremonyTime) : ""].filter(Boolean).join(" · ")}</div>
          <h3 className="panel-title" style={{ fontSize: 28, margin: "4px 0 0" }}>{client.full}</h3>
          {client.venueAddress && <div style={{ fontSize: 15 }}>{client.venueAddress}</div>}
          <div className="text-muted" style={{ fontSize: 15 }}>Coordinator: {[plannerName, studioName, plannerPhone].filter(Boolean).join(" · ")}</div>
        </div>
        <section>
          <div className="label" style={{ marginBottom: 6 }}>The couple</div>
          {(client.partners || []).map((p, i) => <div key={i} className="list-split list-row" style={{ fontSize: 15.5 }}><span>{p.name}</span><span className="figure">{p.phone || "—"}</span></div>)}
        </section>
        <section>
          <div className="label" style={{ marginBottom: 6 }}>People</div>
          {ps.length === 0 && <div className="empty">No one listed yet.</div>}
          {ps.map(p => <div key={p.id} className="list-split list-row" style={{ fontSize: 15.5 }}><span>{p.name} <span className="text-muted">· {p.role}{p.notes ? " · " + p.notes : ""}</span></span><span className="figure">{p.phone || "—"}</span></div>)}
        </section>
        <section>
          <div className="label" style={{ marginBottom: 6 }}>Vendors</div>
          {vs.length === 0 && <div className="empty">No booked vendors yet.</div>}
          {vs.map(v => <div key={v.id} className="list-split list-row" style={{ fontSize: 15.5 }}><span>{v.name} <span className="text-muted">· {v.category}{v.contact && v.contact !== "—" ? " · " + v.contact : ""}</span></span><span className="figure">{v.phone && v.phone !== "—" ? v.phone : "—"}</span></div>)}
        </section>
        {run.length > 0 && (
          <section>
            <div className="label" style={{ marginBottom: 6 }}>{TIMELINE_LABELS.day}</div>
            {run.map(r => <div key={r.id} className="list-row" style={{ fontSize: 15, display: "flex", gap: 14 }}><span className="figure-strong" style={{ width: 132, flex: "none" }}>{prettyRange(r.time, r.end)}</span><span>{r.event}{r.person ? " · " + r.person : ""}{r.vendor ? " · " + r.vendor : ""}</span></div>)}
          </section>
        )}
        {layouts.length > 0 && (
          <section aria-label="Venue layout">
            <div className="label" style={{ marginBottom: 6 }}>Venue layout</div>
            {layouts.map(l => <div key={l.id} className="list-row" style={{ fontSize: 15.5 }}>{l.caption || (l.file && l.file.name) || "Layout"}{l.areas.length ? <span className="text-muted"> · {l.areas.join(", ")}</span> : null}{l.file && !isImageFile(l.file) ? <span className="text-muted"> · PDF, print separately</span> : null}</div>)}
            {layouts.filter(l => l.dataUrl || isImageFile(l.file)).map(l => (
              <figure key={l.id} className="print-layout"><LayoutPreview layout={l} height={420} /><figcaption className="field-hint">{l.caption}</figcaption></figure>
            ))}
          </section>
        )}
      </div>
    </Dialog>
  );
}
const formatTime = v => prettyTime(v);
